import React, {
  useMemo,
  useEffect,
  useRef,
  useCallback,
  useDeferredValue,
  startTransition,
} from "react";
import { useLegendState as useState } from "@/hooks/useLegendState";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import {
  Search, LayoutGrid, List, Table2, SlidersHorizontal, Video, Play,
  ArrowUp, ArrowDown, MoreHorizontal, Pencil, Trash2,
  FileText, Link2, ExternalLink, Youtube, File, Image as ImageIcon,
  ChevronDown, ChevronUp, ChevronRight, Plus, BookOpen, Layers, Sparkles,
  GraduationCap, ChefHat, Briefcase, Palette, Heart, Wallet,
  TrendingUp, Compass, GitCompare, Lightbulb, Target, Users,
  Lock, Zap, Copy, Merge, Type, MessageCircle, Tag, Folder, FolderOpen, FolderPlus, Brain, X, Share2, Star,
  User, Globe, Newspaper, BookMarked, Wand2, Bot, Loader2, GripVertical
} from "lucide-react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { cn } from "@/lib/utils";
import { SiNotion, SiGoogledrive, SiDropbox, SiGithub } from "react-icons/si";
import { GoogleDrivePicker } from "@/components/GoogleDrivePicker";
import { FileUploadToNote } from "@/components/FileUploadToNote";
import { Progress } from "@/components/ui/progress";
import { useViewPreferences } from "@/hooks/use-view-preferences";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useMastermind } from "@/contexts/MastermindContext";
import { useLibrary } from "@/contexts/LibraryContext";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { formatDistanceToNow } from "date-fns";
import { knowledgeBaseService, type KnowledgeBaseItem } from "@/services/knowledgeBaseService";
import { sourcesService } from "@/services/sourcesService";
import { mastermindSpacesService } from "@/services/mastermindSpacesService";
import { topicsService } from "@/services/topicsService";
import {
  linkLibraryDocumentToSpace,
  linkLibraryDocumentToTopic,
  moveLibraryDocumentToFolder,
} from "@/library/libraryDocumentDndActions";
import { libraryDocumentDropHooksRef } from "@/library/libraryRouteDragEnd";
import { chatThreadsService } from "@/services/chatThreadsService";
import { tagsService } from "@/services/tagsService";
import { foldersService, type LibraryFolder } from "@/services/foldersService";
import {
  ensureLibraryFolderByName,
  getGoogleDriveImportRootFolderName,
} from "@/lib/ensureLibraryFolderByName";
import { ragService } from "@/services/ragService";
import type { Source, MastermindSpace, Topic, ChatThread } from "@shared/schema";
import EmptyState from "@/components/EmptyState";
import { EntityLockButton } from "@/components/EntityLockButton";
import { PinVerificationDialog } from "@/components/PinVerificationDialog";
import { RelationshipInspectorDrawer } from "@/components/RelationshipInspectorDrawer";
import FilePreview from "@/components/FilePreview";
import { useToast } from "@/hooks/use-toast";
import { 
  ALL_BLUEPRINTS, 
  EDITION_BLUEPRINTS, 
  RESEARCH_BLUEPRINTS,
  type Blueprint
} from "@/types/blueprints";
import { AIToolList } from "@/components/AIToolCard";
import { QuickPromptModal } from "@/components/QuickPromptModal";
import { WorkflowBuilderModal } from "@/components/WorkflowBuilderModal";
import { ALL_AI_TOOLS } from "@/data/aiToolkit";
import type { AITool, QuickPrompt, Workflow } from "@/types/aiToolkit";
import { AI_TOOL_CATEGORIES } from "@/types/aiToolkit";
import { VoiceInputButton } from "@/components/VoiceInputButton";
import { aiWorkflowsService } from "@/services/aiWorkflowsService";
import {useMergedKnowledgeDocuments} from "@/hooks/useMergedKnowledgeDocuments";
import {
  kbItemToUnifiedLibraryItem,
  sourceToUnifiedLibraryItem,
  type UnifiedLibraryItem,
} from "@/lib/kbItemToUnifiedLibraryItem";
import {
  LibraryBrowseDocumentRow,
  LibraryBrowseDocumentMenu,
} from "@/components/library/LibraryBrowseDocumentRow";
import {
  LibraryDocumentsVirtualList,
  LIBRARY_DOCUMENT_VIRTUALIZE_THRESHOLD,
} from "@/components/library/LibraryDocumentsVirtualList";
import type { LibraryDocumentActions } from "@/components/LibraryDocumentRow";
import type { DocumentActionsItem } from "@/components/DocumentActionsMenuContent";
import { unifiedItemToDocumentActions } from "@/lib/unifiedLibraryItemToActions";
import { useCopyPublicDocumentLink } from "@/hooks/useCopyPublicDocumentLink";
import { featuredVideosService, type FeaturedVideo } from "@/services/featuredVideosService";
import { LibraryBreadcrumbs } from "@/components/library/LibraryBreadcrumbs";
import { IconSearch, IconSelectMenu } from "@/components/CompactToolbar";
import {
  useLibraryFavorites,
  useLibraryRecent,
  orderByRecentIds,
} from "@/hooks/useLibraryQuickAccess";
import { useSourceOfTruth } from "@/hooks/useSourceOfTruth";
import { toggleDocumentSourceOfTruth } from "@/services/sourceOfTruthService";
import type { LibraryDocRef } from "@/lib/libraryQuickAccess";
import { LibraryFolderStrip } from "@/components/library/LibraryFolderStrip";
import {
  LibrarySpacesSection,
  librarySpacesFromMastermind,
  type LibrarySpaceCard,
} from "@/components/library/LibrarySpacesSection";
import { LibraryDocumentsSection } from "@/components/library/LibraryDocumentsSection";
import {
  DroppableLibraryFolder,
  DroppableLibrarySpace,
} from "@/components/library/LibraryDroppables";

type LibrarySortField = "title" | "type" | "topic" | "createdAt" | "updatedAt";
type LibraryFilter = "all" | "notes" | "references" | "experts" | "tutorials";
type LibrarySection = "content" | "blueprints" | "ai-toolkit" | "super-prompts";
type BlueprintFilter = "all" | "edition" | "research";
type AIToolkitFilter = "all" | "quick-prompt" | "workflow";
type BlueprintSortField = "name" | "category" | "type";
type BlueprintViewMode = "grid" | "list";

function isExpertItem(type: string | null | undefined, tags: string[] | null | undefined): boolean {
  const lowerType = type?.toLowerCase() || "";
  const lowerTags = Array.isArray(tags) ? tags.map(t => t.toLowerCase()) : [];
  return lowerType.includes("sme") || lowerType.includes("expert") || 
         lowerTags.includes("sme") || lowerTags.includes("expert");
}

function getTypeIcon(type: string, itemType: "note" | "reference", tags?: string[], url?: string | null) {
  if (itemType === "note") {
    if (type?.toLowerCase() === "youtube") return <Youtube className="h-4 w-4 text-red-500" />;
    return <FileText className="h-4 w-4 text-blue-500" />;
  }
  
  const lowerType = type?.toLowerCase() || "";
  const lowerUrl = url?.toLowerCase() || "";
  
  if (isExpertItem(type, tags)) {
    return <User className="h-4 w-4 text-indigo-500" />;
  }
  if (lowerType.includes("notion") || lowerUrl.includes("notion.so") || lowerUrl.includes("notion.site")) {
    return <SiNotion className="h-4 w-4" />;
  }
  if (lowerType.includes("google") || lowerType.includes("drive") || lowerUrl.includes("drive.google.com") || lowerUrl.includes("docs.google.com")) {
    return <SiGoogledrive className="h-4 w-4 text-yellow-500" />;
  }
  if (lowerType.includes("dropbox") || lowerUrl.includes("dropbox.com")) {
    return <SiDropbox className="h-4 w-4 text-blue-600" />;
  }
  if (lowerType.includes("github") || lowerUrl.includes("github.com")) {
    return <SiGithub className="h-4 w-4" />;
  }
  if (lowerType.includes("youtube") || lowerType.includes("video") || lowerUrl.includes("youtube.com") || lowerUrl.includes("youtu.be")) {
    return <Youtube className="h-4 w-4 text-red-500" />;
  }
  if (lowerType.includes("url") || lowerType.includes("link") || lowerType.includes("webpage")) {
    return <Link2 className="h-4 w-4 text-green-500" />;
  }
  if (lowerType.includes("article") || lowerType.includes("news")) {
    return <Newspaper className="h-4 w-4 text-cyan-500" />;
  }
  if (lowerType.includes("book")) {
    return <BookMarked className="h-4 w-4 text-amber-600" />;
  }
  if (lowerType.includes("image")) {
    return <ImageIcon className="h-4 w-4 text-purple-500" />;
  }
  if (lowerType.includes("pdf") || lowerType.includes("document")) {
    return <File className="h-4 w-4 text-orange-500" />;
  }
  return <Globe className="h-4 w-4 text-gray-500" />;
}

function getTypeLabel(type: string, itemType: "note" | "reference", tags?: string[], url?: string | null): string {
  if (itemType === "note") {
    if (type?.toLowerCase() === "youtube") return "YouTube";
    return "Note";
  }
  
  if (isExpertItem(type, tags)) return "Expert";
  
  const lowerType = type?.toLowerCase() || "";
  const lowerUrl = url?.toLowerCase() || "";
  
  if (lowerType.includes("notion") || lowerUrl.includes("notion.so") || lowerUrl.includes("notion.site")) return "Notion";
  if (lowerType.includes("google") || lowerType.includes("drive") || lowerUrl.includes("drive.google.com") || lowerUrl.includes("docs.google.com")) return "Drive";
  if (lowerType.includes("dropbox") || lowerUrl.includes("dropbox.com")) return "Dropbox";
  if (lowerType.includes("github") || lowerUrl.includes("github.com")) return "GitHub";
  if (lowerType.includes("youtube") || lowerType.includes("video") || lowerUrl.includes("youtube.com") || lowerUrl.includes("youtu.be")) return "Video";
  if (lowerType.includes("url") || lowerType.includes("link") || lowerType.includes("webpage")) return "URL";
  if (lowerType.includes("article") || lowerType.includes("news")) return "Article";
  if (lowerType.includes("book")) return "Book";
  if (lowerType.includes("image")) return "Image";
  if (lowerType.includes("pdf") || lowerType.includes("document")) return "Document";
  return "Reference";
}

// Wraps a document card/row so it can be dragged onto folders (grid/list use a div wrapper)
function DraggableLibraryItem({
  item,
  children,
}: {
  item: UnifiedLibraryItem;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `doc-${item.id}`,
    data: { documentId: item.id, itemType: item.itemType },
  });
  return (
    <div
      ref={setNodeRef}
      style={transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined}
      className={cn("flex items-stretch gap-0 min-h-0", isDragging && "opacity-50 z-50")}
    >
      {/* In-flow drag handle – always visible, not covered by Card */}
      <div
        {...attributes}
        {...listeners}
        className="shrink-0 flex items-center justify-center w-7 cursor-grab active:cursor-grabbing bg-muted/70 hover:bg-muted text-muted-foreground self-stretch rounded-l-md"
        onClick={(e) => e.stopPropagation()}
        title="Drag to move to folder"
      >
        <GripVertical className="h-4 w-4" aria-hidden />
      </div>
      <div className="flex-1 min-w-0">
        {children}
      </div>
    </div>
  );
}

// For table: draggable row with drag-handle cell
function DraggableTableDocumentRow({
  item,
  onClick,
  children,
}: {
  item: UnifiedLibraryItem;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `doc-${item.id}`,
    data: { documentId: item.id, itemType: item.itemType },
  });
  return (
    <TableRow
      ref={setNodeRef}
      style={transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined}
      className={cn("group cursor-pointer hover:bg-muted/50", isDragging && "opacity-50")}
      onClick={onClick}
    >
      <TableCell
        {...attributes}
        {...listeners}
        className="w-8 cursor-grab active:cursor-grabbing p-1"
        onClick={(e) => e.stopPropagation()}
        title="Drag to move to folder"
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </TableCell>
      {children}
    </TableRow>
  );
}

function TutorialPlayPreview({
  video,
  onOpen,
  className,
}: {
  video: FeaturedVideo;
  onOpen: (v: FeaturedVideo) => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={cn(
        "group relative aspect-video w-full overflow-hidden rounded-md bg-black text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className
      )}
      onClick={() => onOpen(video)}
      aria-label={`Play ${video.title}`}
    >
      <video
        src={video.url}
        muted
        preload="metadata"
        playsInline
        className="pointer-events-none h-full w-full object-contain"
      />
      <span className="absolute inset-0 flex items-center justify-center bg-black/35 transition-colors group-hover:bg-black/50">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg ring-2 ring-background/90">
          <Play className="h-7 w-7 fill-current pl-0.5" aria-hidden />
        </span>
      </span>
    </button>
  );
}

export default function Library() {
  const { user } = useAuth();
  const { currentWorkspaceId } = useWorkspace();
  const { selectedFolderId, setSelectedFolderId, selectedDocumentId, setSelectedDocumentId, setSelectedReferenceId } = useLibrary();
  const { setIsCollapsed } = useMastermind();
  const { completeMilestone, hasMilestone } = useOnboarding();
  const [, navigate] = useLocation();
  const searchString = useSearch();
  
  // Use ref to prevent multiple triggers during same session
  const hasTriggeredMilestone = useRef(false);
  
  // Trigger explore_library milestone when user first visits Library (unlocks Tools)
  useEffect(() => {
    // Check ref to prevent re-triggering on context re-renders
    if (!hasTriggeredMilestone.current && !hasMilestone('explore_library')) {
      hasTriggeredMilestone.current = true;
      completeMilestone('explore_library');
    }
  }, []); // Empty deps - only run once on mount
  
  
  const {
    viewMode,
    setViewMode,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
  } = useViewPreferences<LibrarySortField>({
    storageKey: "library",
    defaultViewMode: "grid",
    defaultSortField: "updatedAt",
    defaultSortDirection: "desc",
  });

  const { toast } = useToast();
  const { copyPublicLink } = useCopyPublicDocumentLink();
  const queryClient = useQueryClient();
  const { docIds: sourceOfTruthDocIds } = useSourceOfTruth();
  const sourceOfTruthIdSet = useMemo(
    () => new Set(sourceOfTruthDocIds),
    [sourceOfTruthDocIds]
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [tutorialPlaybackVideo, setTutorialPlaybackVideo] = useState<FeaturedVideo | null>(null);
  const [activeFilter, setActiveFilter] = useState<LibraryFilter>("all");
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [librarySection, setLibrarySection] = useState<LibrarySection>(() => {
    const params = new URLSearchParams(searchString);
    const section = params.get("section");
    if (section === "blueprints") return "blueprints";
    if (section === "ai-toolkit" || section === "workflows") return "ai-toolkit";
    return "content";
  });
  const [blueprintFilter, setBlueprintFilter] = useState<BlueprintFilter>("all");
  const [aiToolkitFilter, setAiToolkitFilter] = useState<AIToolkitFilter>(() => {
    const params = new URLSearchParams(searchString);
    const section = params.get("section");
    if (section === "workflows") return "workflow";
    return "all";
  });
  const [aiToolkitCategory, setAiToolkitCategory] = useState<string>("All");
  const [selectedAITool, setSelectedAITool] = useState<AITool | null>(null);
  const [quickPromptModalOpen, setQuickPromptModalOpen] = useState(false);
  const [workflowBuilderOpen, setWorkflowBuilderOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<Workflow | null>(null);
  const [blueprintSearch, setBlueprintSearch] = useState("");
  const [blueprintViewMode, setBlueprintViewMode] = useState<BlueprintViewMode>("grid");
  const [blueprintSortField, setBlueprintSortField] = useState<BlueprintSortField>("name");
  const [blueprintSortDirection, setBlueprintSortDirection] = useState<"asc" | "desc">("asc");
  const [aiToolkitViewMode, setAiToolkitViewMode] = useState<BlueprintViewMode>("grid");
  const [showBuiltInWorkflows, setShowBuiltInWorkflows] = useState(true);
  const [isLoadingWorkflows, setIsLoadingWorkflows] = useState(false);
  const [workflowsError, setWorkflowsError] = useState<string | null>(null);

  const [filePreviewOpen, setFilePreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ id: string; filename: string; url: string } | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  // Documents dropped on a folder are hidden from the list until the move API completes (optimistic UI)
  const [optimisticallyMovedIds, setOptimisticallyMovedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    libraryDocumentDropHooksRef.current = {
      onOptimisticMove: (itemId) => {
        setOptimisticallyMovedIds((prev) => new Set(prev).add(itemId));
      },
      onClearOptimisticMove: (itemId) => {
        setOptimisticallyMovedIds((prev) => {
          const next = new Set(prev);
          next.delete(itemId);
          return next;
        });
      },
    };
    return () => {
      libraryDocumentDropHooksRef.current = null;
    };
  }, []);

  const lastClickedIndexRef = useRef<number | null>(null);
  const lastClickedIdRef = useRef<string | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renamingItem, setRenamingItem] = useState<UnifiedLibraryItem | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeSourceItem, setMergeSourceItem] = useState<UnifiedLibraryItem | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState<string>("");
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [inspectorItem, setInspectorItem] = useState<UnifiedLibraryItem | null>(null);
  const [filterSpaceId, setFilterSpaceId] = useState<string | null>(null);
  const [filterTopicId, setFilterTopicId] = useState<string | null>(null);
  const [filterLibraryView, setFilterLibraryView] = useState<"favorites" | "recent" | null>(null);

  const { favorites, favoriteIdSet, isFavorite, toggleFavorite } = useLibraryFavorites();
  const { recentIds, touchRecent } = useLibraryRecent();
  
  const [expertDialogOpen, setExpertDialogOpen] = useState(false);
  const [newExpertName, setNewExpertName] = useState("");
  const [newExpertRole, setNewExpertRole] = useState("");
  const [newExpertBio, setNewExpertBio] = useState("");
  const [childDocsDialogOpen, setChildDocsDialogOpen] = useState(false);
  const [selectedExpertItem, setSelectedExpertItem] = useState<UnifiedLibraryItem | null>(null);
  const [addReferenceDialogOpen, setAddReferenceDialogOpen] = useState(false);
  const [newReferenceUrl, setNewReferenceUrl] = useState("");
  const [newReferenceTitle, setNewReferenceTitle] = useState("");
  const [showDrivePicker, setShowDrivePicker] = useState(true);
  const [isCreatingNotes, setIsCreatingNotes] = useState(false);
  const [noteCreationProgress, setNoteCreationProgress] = useState<{
    total: number;
    completed: number;
    current: string;
    failed: string[];
  } | null>(null);
  const [pendingDocumentIdToOpen, setPendingDocumentIdToOpen] = useState<string | null>(null);
  const [showPinDialogForDocument, setShowPinDialogForDocument] = useState(false);
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  /** When set, new folder is created inside this folder; when null, use current view (selectedFolderId or root). */
  const [newFolderDialogParentId, setNewFolderDialogParentId] = useState<string | null>(null);
  const [moveToFolderDialogOpen, setMoveToFolderDialogOpen] = useState(false);
  const [moveToFolderSearchQuery, setMoveToFolderSearchQuery] = useState("");
  const [expandedMoveFolderIds, setExpandedMoveFolderIds] = useState<Set<string>>(new Set());
  const [editFolderDialogOpen, setEditFolderDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<LibraryFolder | null>(null);
  const [editFolderName, setEditFolderName] = useState("");

  const lastLibraryUrlSyncKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(searchString);
    const section = params.get("section");
    const spaceId = params.get("space");
    const topicId = params.get("topic");
    const viewParam = params.get("view");
    const libraryView =
      viewParam === "favorites" || viewParam === "recent" ? viewParam : null;

    const platform = params.get("platform");
    const syncKey = `${section ?? ""}|${spaceId ?? ""}|${topicId ?? ""}|${libraryView ?? ""}|${platform ?? ""}`;
    if (lastLibraryUrlSyncKeyRef.current === syncKey) return;
    lastLibraryUrlSyncKeyRef.current = syncKey;

    if (section === "integrations") {
      navigate("/communication/google-drive");
      return;
    } else if (section === "blueprints") {
      setLibrarySection("blueprints");
      setFilterSpaceId(null);
      setFilterTopicId(null);
      setFilterLibraryView(null);
    } else if (section === "ai-toolkit") {
      setLibrarySection("ai-toolkit");
      setFilterSpaceId(null);
      setFilterTopicId(null);
      setFilterLibraryView(null);
    } else if (section === "workflows") {
      setLibrarySection("ai-toolkit");
      setAiToolkitFilter("workflow");
      setFilterSpaceId(null);
      setFilterTopicId(null);
      setFilterLibraryView(null);
    } else if (spaceId) {
      startTransition(() => {
        setLibrarySection("content");
        setFilterSpaceId(spaceId);
        setFilterTopicId(null);
        setFilterLibraryView(null);
      });
    } else if (topicId) {
      startTransition(() => {
        setLibrarySection("content");
        setFilterSpaceId(null);
        setFilterTopicId(topicId);
        setSelectedFolderId(null);
        setFilterLibraryView(null);
      });
    } else if (libraryView) {
      startTransition(() => {
        setLibrarySection("content");
        setFilterSpaceId(null);
        setFilterTopicId(null);
        setSelectedFolderId(null);
        setFilterLibraryView(libraryView);
      });
    } else if (!section && !spaceId && !topicId && !libraryView) {
      startTransition(() => {
        setLibrarySection("content");
        setFilterSpaceId(null);
        setFilterTopicId(null);
        setFilterLibraryView(null);
      });
    }
  }, [searchString, setSelectedFolderId, navigate]);

  /*const { data: kbItems = [], isLoading: kbLoading } = useQuery<KnowledgeBaseItem[]>({
    queryKey: ["/api/knowledge-base", user?.id],
    queryFn: async () => {
      if (!user) return [];
      return await knowledgeBaseService.getKnowledgeBaseItems();
    },
    enabled: !!user,
    staleTime: 30000, // Cache for 30 seconds to reduce database load
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });*/
    const {
        allDocuments: kbItems,
        isLoading: kbLoading,
    } = useMergedKnowledgeDocuments({ enabled: true });

  const { data: sources = [], isLoading: sourcesLoading } = useQuery<Source[]>({
    queryKey: ["/api/sources", user?.id, currentWorkspaceId ?? "personal"],
    queryFn: async () => {
      if (!user) return [];
      return await sourcesService.getSources(undefined, currentWorkspaceId ?? null);
    },
    enabled: !!user,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  });

  const { data: spaces = [] } = useQuery<MastermindSpace[]>({
    queryKey: ["/api/spaces", user?.id, currentWorkspaceId ?? "personal"],
    queryFn: async () => {
      if (!user) return [];
      return await mastermindSpacesService.getMastermindSpaces(currentWorkspaceId ?? null);
    },
    enabled: !!user,
  });

  // Check if we're inside the SPACES virtual folder or a specific space
  const isInSpacesFolder = selectedFolderId === "spaces";
  const selectedSpaceId = selectedFolderId?.startsWith("space-") 
    ? selectedFolderId.replace("space-", "") 
    : null;
  const isInsideSpecificSpace = selectedSpaceId !== null;

  // Fetch the selected space data to get linkedSourceIds
  const { data: selectedSpace } = useQuery<MastermindSpace | null>({
    queryKey: ["/api/spaces", selectedSpaceId, currentWorkspaceId ?? "personal"],
    queryFn: async () => {
      if (!selectedSpaceId) return null;
      return await mastermindSpacesService.getMastermindSpace(selectedSpaceId, currentWorkspaceId ?? null);
    },
    enabled: !!selectedSpaceId,
  });

  const { data: topics = [] } = useQuery<Topic[]>({
    queryKey: ["/api/topics/all", user?.id],
    queryFn: async () => {
      if (!user) return [];
      return await topicsService.getAllTopics();
    },
    enabled: !!user,
  });

  // Thread summary type for lightweight listing (no messages)
  type ThreadSummary = Omit<ChatThread, 'messages'> & { messageCount: number };

  const { data: threads = [] } = useQuery<ThreadSummary[]>({
    queryKey: ["/api/chat-thread-summaries", user?.id, currentWorkspaceId ?? "personal"],
    queryFn: async () => {
      if (!user) return [];
      return await chatThreadsService.getThreadSummaries(currentWorkspaceId ?? null);
    },
    enabled: !!user,
    staleTime: 30000,
  });

  const { data: allTags = [] } = useQuery({
    queryKey: ["/api/tags", user?.id],
    queryFn: async () => {
      if (!user) return [];
      return await tagsService.getTags();
    },
    enabled: !!user,
    staleTime: 60000, // Cache tags for 1 minute
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  // Fetch folders — same as RightSidebar: Personal = all folders (no arg), Workspace = that workspace's folders
  const folderWorkspaceParam = currentWorkspaceId ?? undefined;
  const { data: folders = [] } = useQuery<LibraryFolder[]>({
    queryKey: ["/api/library-folders", user?.id, folderWorkspaceParam === undefined ? "all" : folderWorkspaceParam ?? "personal"],
    queryFn: async () => {
      if (!user) return [];
      return await foldersService.getFolders(folderWorkspaceParam);
    },
    enabled: !!user,
    staleTime: 60000, // Cache for 1 minute
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const { data: pinStatus } = useQuery<{ hasPinSet: boolean; isSessionValid: boolean }>({
    queryKey: ["pin-status"],
    queryFn: async () => {
      const { pinService } = await import("@/services/pinService");
      return pinService.getStatus();
    },
    enabled: !!user,
  });

  // Fetch user-created workflows
  const { data: userWorkflows = [], isLoading: isLoadingUserWorkflows, error: workflowsErrorQuery } = useQuery<Workflow[]>({
    queryKey: ["/api/ai-workflows", user?.id],
    queryFn: async () => {
      if (!user) return [];
      return await aiWorkflowsService.getWorkflows();
    },
    enabled: !!user && librarySection === "ai-toolkit",
    staleTime: 30000, // Cache for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  // Handle workflows error
  useEffect(() => {
    if (workflowsErrorQuery) {
      const errorMessage = workflowsErrorQuery instanceof Error ? workflowsErrorQuery.message : "Failed to load workflows";
      setWorkflowsError(errorMessage);
    }
  }, [workflowsErrorQuery]);

  const spacesMap = useMemo(() => {
    const map = new Map<string, MastermindSpace>();
    spaces.forEach((space) => map.set(space.id, space));
    return map;
  }, [spaces]);

  const topicsMap = useMemo(() => {
    const map = new Map<string, Topic>();
    topics.forEach((topic) => map.set(topic.id, topic));
    return map;
  }, [topics]);

  // Create topicNameMap for DocumentItem (similar to DocumentGrid)
  const topicNameMap = useMemo(() => {
    const map: Record<string | number, string> = {};
    topics.forEach(topic => {
      map[topic.id] = topic.name;
      // Also add as number if topic.id is a string, for compatibility
      if (typeof topic.id === 'string') {
        const numId = Number(topic.id);
        if (!isNaN(numId)) {
          map[numId] = topic.name;
        }
      }
    });
    return map;
  }, [topics]);

  const threadsMap = useMemo(() => {
    const map = new Map<string, ThreadSummary>();
    threads.forEach((thread) => map.set(thread.id, thread));
    return map;
  }, [threads]);

  const tagsMap = useMemo(() => {
    const map = new Map<string, any>();
    allTags.forEach((tag: any) => map.set(tag.id, tag));
    return map;
  }, [allTags]);

  const unifiedItems: UnifiedLibraryItem[] = useMemo(() => {
    const items: UnifiedLibraryItem[] = [];

    kbItems.forEach((item: KnowledgeBaseItem) => {
      items.push(kbItemToUnifiedLibraryItem(item));
    });

    sources.forEach((source: Source) => {
      items.push(sourceToUnifiedLibraryItem(source));
    });

    return items;
  }, [kbItems, sources]);

  const unifiedItemsById = useMemo(() => {
    const map = new Map<string, UnifiedLibraryItem>();
    for (const item of unifiedItems) {
      map.set(item.id, item);
    }
    return map;
  }, [unifiedItems]);

  const itemsByTopicId = useMemo(() => {
    const map = new Map<string, UnifiedLibraryItem[]>();
    for (const item of unifiedItems) {
      for (const topicId of item.linkedTopicIds) {
        if (!topicId) continue;
        const list = map.get(topicId) ?? [];
        list.push(item);
        map.set(topicId, list);
      }
    }
    return map;
  }, [unifiedItems]);

  // DS EDIT 5aug26 — topic sort support: first linked topic's name per item (unlinked items sort last)
  const itemTopicName = useCallback(
    (item: UnifiedLibraryItem): string => {
      for (const topicId of item.linkedTopicIds) {
        const name = topicNameMap[topicId];
        if (name) return name;
      }
      return "";
    },
    [topicNameMap]
  );

  // Get all documents for merge dialog
  const allDocumentsForMerge = useMemo(() => {
    return unifiedItems
      .filter(item => item.itemType === "note")
      .map(item => item.originalItem as KnowledgeBaseItem);
  }, [unifiedItems]);

  // Filter by folder/space/topic/search (but not by activeFilter - that's for display)
  // Google Drive-like behavior: show only items at current level
  const baseFilteredItems = useMemo(() => {
    let filtered = unifiedItems;

    if (filterTopicId) {
      const seen = new Set<string>();
      const topicItems: UnifiedLibraryItem[] = [];
      const add = (item: UnifiedLibraryItem) => {
        if (!seen.has(item.id)) {
          seen.add(item.id);
          topicItems.push(item);
        }
      };
      for (const item of itemsByTopicId.get(filterTopicId) ?? []) {
        add(item);
      }
      filtered = topicItems;
    } else if (filterSpaceId) {
      filtered = filtered.filter(
        (item) =>
          Array.isArray(item.linkedSpaceIds) && item.linkedSpaceIds.includes(filterSpaceId)
      );
    } else if (filterLibraryView === "favorites") {
      filtered = unifiedItems.filter((item) => favoriteIdSet.has(item.id));
    } else if (filterLibraryView === "recent") {
      const recentSet = new Set(recentIds);
      filtered = unifiedItems.filter((item) => recentSet.has(item.id));
    } else {
      // Google Drive-like hierarchical filtering when not in URL space/topic filter mode
      filtered = filtered.filter((item) => {
        if (isInSpacesFolder) {
          return false;
        }
        if (selectedSpaceId) {
          const isInLinkedSpaceIds =
            Array.isArray(item.linkedSpaceIds) && item.linkedSpaceIds.includes(selectedSpaceId);
          const isInSpaceLinkedSourceIds =
            selectedSpace?.linkedSourceIds?.includes(item.id) || false;
          const isNoteWithSpaceId =
            item.itemType === "note" &&
            (item.originalItem as KnowledgeBaseItem)?.spaceId === selectedSpaceId;
          return (
            isInLinkedSpaceIds ||
            isInSpaceLinkedSourceIds ||
            isNoteWithSpaceId
          );
        }
        if (selectedFolderId) {
          return item.folderId === selectedFolderId;
        }
        return !item.folderId;
      });
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.title.toLowerCase().includes(query) ||
          item.content?.toLowerCase().includes(query) ||
          (Array.isArray(item.tags) && item.tags.some((tag) => tag.toLowerCase().includes(query)))
      );
    }

    return filtered;
  }, [
    unifiedItems,
    selectedFolderId,
    isInSpacesFolder,
    selectedSpaceId,
    selectedSpace,
    itemsByTopicId,
    unifiedItemsById,
    filterSpaceId,
    filterTopicId,
    filterLibraryView,
    favoriteIdSet,
    recentIds,
    searchQuery,
  ]);

  // Get folders to display at current level (Google Drive-like behavior)
  // - At root: show top-level folders (parentId === null)
  // - Inside a folder: show child folders of that folder
  // - Inside SPACES: show nothing (spaces are displayed separately)
  // - Inside a specific space: show nothing (just documents)
  const nestedFolders = useMemo(() => {
    if (isInSpacesFolder || selectedSpaceId) {
      // Inside SPACES or a specific space: no real folders to show
      return [];
    }
    if (selectedFolderId) {
      // Inside a folder: show its children
      return folders.filter(f => f.parentId === selectedFolderId).sort((a, b) => a.sortOrder - b.sortOrder);
    } else {
      // At root level: show top-level folders (those with no parent)
      return folders.filter(f => !f.parentId).sort((a, b) => a.sortOrder - b.sortOrder);
    }
  }, [folders, selectedFolderId, isInSpacesFolder, selectedSpaceId]);

  // Virtual "space folders" to display when inside SPACES
  const spaceVirtualFolders = useMemo(() => {
    if (!isInSpacesFolder) return [];
    return spaces.map(space => ({
      id: `space-${space.id}`,
      name: space.name,
      spaceId: space.id,
      description: space.description,
    }));
  }, [spaces, isInSpacesFolder]);

  // Get item counts for nested folders
  const folderItemCounts = useMemo(() => {
    const counts = new Map<string, number>();
    unifiedItems.forEach((item) => {
      if (item.folderId) {
        counts.set(item.folderId, (counts.get(item.folderId) || 0) + 1);
      }
    });
    return counts;
  }, [unifiedItems]);

  const filteredAndSortedItems = useMemo(() => {
    let filtered = baseFilteredItems;

    if (activeFilter === "notes") {
      filtered = filtered.filter((item) => item.itemType === "note");
    } else if (activeFilter === "references") {
      filtered = filtered.filter((item) => item.itemType === "reference");
    } else if (activeFilter === "experts") {
      filtered = filtered.filter((item) => isExpertItem(item.type, item.tags));
    } else if (activeFilter === "tutorials") {
      filtered = [];
    }

    if (filterLibraryView === "recent") {
      return orderByRecentIds(filtered, recentIds);
    }

    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "title":
          comparison = a.title.localeCompare(b.title);
          break;
        case "type":
          comparison = a.type.localeCompare(b.type);
          break;
        case "topic": {
          const aName = itemTopicName(a);
          const bName = itemTopicName(b);
          if (!aName && !bName) comparison = 0;
          else if (!aName) comparison = 1;
          else if (!bName) comparison = -1;
          else comparison = aName.localeCompare(bName);
          break;
        }
        case "createdAt":
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case "updatedAt":
        default:
          comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return filtered;
  }, [baseFilteredItems, activeFilter, sortField, sortDirection, filterLibraryView, recentIds, itemTopicName]);

  const { data: tutorialVideos = [], isLoading: tutorialVideosLoading } = useQuery({
    queryKey: ["featured-videos", user?.id],
    queryFn: () => featuredVideosService.getFeaturedVideos(),
    enabled: !!user && librarySection === "content",
    staleTime: 60_000,
  });

  const filteredTutorialVideos = useMemo(() => {
    let list = tutorialVideos;
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((v) => v.title.toLowerCase().includes(q));
    }
    const sorted = [...list].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "title":
          comparison = a.title.localeCompare(b.title);
          break;
        case "type":
        case "topic":
          comparison = 0;
          break;
        case "createdAt":
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case "updatedAt":
        default:
          comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });
    return sorted;
  }, [tutorialVideos, searchQuery, sortField, sortDirection]);

  useEffect(() => {
    if (activeFilter === "tutorials") {
      setSelectedItems(new Set());
    }
  }, [activeFilter]);

  // Type for virtual space folders
  type SpaceVirtualFolder = {
    id: string;
    name: string;
    spaceId: string;
    description?: string | null;
  };

  // Type for the main SPACES virtual folder entry at root
  type SpacesRootFolder = {
    id: "spaces";
    name: "SPACES";
    spaceCount: number;
  };

  // When searching or narrowing (space/topic/type tabs), show matching documents first; otherwise folders/spaces stay on top.
  const prioritizeLibraryMatches = useMemo(
    () =>
      Boolean(searchQuery.trim()) ||
      Boolean(filterSpaceId) ||
      Boolean(filterTopicId) ||
      Boolean(filterLibraryView) ||
      (activeFilter !== "all" && activeFilter !== "tutorials"),
    [searchQuery, filterSpaceId, filterTopicId, filterLibraryView, activeFilter]
  );

  const containerNameMatches = useCallback(
    (name: string) => {
      const q = searchQuery.trim().toLowerCase();
      const narrowContainersBySearch = prioritizeLibraryMatches && Boolean(q);
      if (!narrowContainersBySearch) return true;
      return name.toLowerCase().includes(q);
    },
    [searchQuery, prioritizeLibraryMatches]
  );

  const sortContainersByField = useCallback(
    <T extends { name?: string | null; updatedAt?: string | Date | null; createdAt?: string | Date | null }>(
      list: T[]
    ): T[] => {
      const sorted = [...list];
      sorted.sort((a, b) => {
        let comparison = 0;
        if (sortField === "title") {
          comparison = (a.name || "").localeCompare(b.name || "");
        } else {
          const dateA =
            sortField === "createdAt"
              ? new Date(String(a.createdAt || a.updatedAt || 0)).getTime()
              : new Date(String(a.updatedAt || a.createdAt || 0)).getTime();
          const dateB =
            sortField === "createdAt"
              ? new Date(String(b.createdAt || b.updatedAt || 0)).getTime()
              : new Date(String(b.updatedAt || b.createdAt || 0)).getTime();
          comparison = dateA - dateB;
        }
        return sortDirection === "asc" ? comparison : -comparison;
      });
      return sorted;
    },
    [sortField, sortDirection]
  );

  const folderContainers = useMemo(
    () =>
      sortContainersByField(
        nestedFolders.filter((f) => containerNameMatches(f.name))
      ),
    [nestedFolders, containerNameMatches, sortContainersByField]
  );

  const getSpaceDocumentCount = useCallback(
    (spaceId: string) =>
      unifiedItems.filter(
        (item) =>
          Array.isArray(item.linkedSpaceIds) && item.linkedSpaceIds.includes(spaceId)
      ).length,
    [unifiedItems]
  );

  const spaceContainers = useMemo((): LibrarySpaceCard[] => {
    if (isInSpacesFolder) {
      return sortContainersByField(
        spaceVirtualFolders
          .filter((sf) => containerNameMatches(sf.name ?? ""))
          .map((sf) => ({
            id: sf.id,
            spaceId: sf.spaceId,
            name: sf.name ?? "Unnamed Space",
            description: sf.description,
            documentCount: getSpaceDocumentCount(sf.spaceId),
          }))
      );
    }
    if (selectedFolderId || selectedSpaceId) {
      return [];
    }
    return sortContainersByField(
      librarySpacesFromMastermind(spaces, getSpaceDocumentCount).filter((s) =>
        containerNameMatches(s.name)
      )
    );
  }, [
    isInSpacesFolder,
    selectedFolderId,
    selectedSpaceId,
    spaceVirtualFolders,
    spaces,
    containerNameMatches,
    sortContainersByField,
    getSpaceDocumentCount,
  ]);

  const documentItems = useMemo(() => {
    const items: UnifiedLibraryItem[] = [];
    for (const item of filteredAndSortedItems) {
      if (optimisticallyMovedIds.has(item.id)) continue;
      items.push(item);
    }
    return items;
  }, [filteredAndSortedItems, optimisticallyMovedIds]);

  const browseDocumentItems = useDeferredValue(documentItems);
  const isBrowseDocumentsPending = browseDocumentItems !== documentItems;
  const useVirtualDocumentList =
    browseDocumentItems.length > LIBRARY_DOCUMENT_VIRTUALIZE_THRESHOLD;
  const isCompactBrowseUi = Boolean(
    filterTopicId || filterSpaceId || filterLibraryView
  );

  const foldersById = useMemo(() => {
    const map = new Map<string, string>();
    for (const folder of folders) {
      map.set(folder.id, folder.name);
    }
    return map;
  }, [folders]);

  const contentNoteIds = useMemo(
    () =>
      librarySection === "content"
        ? browseDocumentItems.filter((i) => i.itemType === "note").map((i) => i.id)
        : [],
    [librarySection, browseDocumentItems]
  );

  const { data: embeddingStatusMap = {} } = useQuery({
    queryKey: ["embedding-status", contentNoteIds.slice().sort().join(",")],
    queryFn: () => ragService.getEmbeddingStatusForDocuments(contentNoteIds),
    enabled: contentNoteIds.length > 0,
    staleTime: 60_000,
  });

  const hasLibraryBrowseContent =
    folderContainers.length > 0 ||
    spaceContainers.length > 0 ||
    documentItems.length > 0;

  // Folder tree for Move to Folder dialog (root → children hierarchy)
  type MoveFolderTreeNode = { id: string; name: string; children: MoveFolderTreeNode[] };
  const moveFolderTree = useMemo((): MoveFolderTreeNode[] => {
    const roots = folders.filter(f => !f.parentId).sort((a, b) => a.sortOrder - b.sortOrder);
    const build = (parentId: string | null): MoveFolderTreeNode[] => {
      const list = parentId === null ? roots : folders.filter(f => f.parentId === parentId).sort((a, b) => a.sortOrder - b.sortOrder);
      return list.map(f => ({ id: f.id, name: f.name, children: build(f.id) }));
    };
    return build(null);
  }, [folders]);

  const moveFolderTreeFiltered = useMemo(() => {
    const q = moveToFolderSearchQuery.trim().toLowerCase();
    if (!q) return moveFolderTree;
    const filter = (nodes: MoveFolderTreeNode[]): MoveFolderTreeNode[] =>
      nodes.flatMap(node => {
        const childFiltered = filter(node.children);
        const selfMatch = node.name.toLowerCase().includes(q);
        if (selfMatch || childFiltered.length > 0)
          return [{ id: node.id, name: node.name, children: childFiltered }];
        return [];
      });
    return filter(moveFolderTree);
  }, [moveFolderTree, moveToFolderSearchQuery]);

  useEffect(() => {
    if (!moveToFolderSearchQuery.trim() || !moveToFolderDialogOpen) return;
    const collectIds = (nodes: MoveFolderTreeNode[]): string[] =>
      nodes.flatMap(n => [n.id, ...collectIds(n.children)]);
    setExpandedMoveFolderIds(new Set(collectIds(moveFolderTreeFiltered)));
  }, [moveToFolderSearchQuery, moveToFolderDialogOpen, moveFolderTreeFiltered]);

  const handleFolderClick = (folderId: string) => {
    setSelectedFolderId(folderId);
    navigate("/library");
  };

  const toggleExpanded = (id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleExpandedMoveFolder = (id: string) => {
    setExpandedMoveFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleMoveToFolderAndClose = (folderId: string | null) => {
    handleBulkMoveToFolder(folderId);
    setMoveToFolderDialogOpen(false);
    setMoveToFolderSearchQuery("");
  };

  const handleMoveToSpaceAndClose = (spaceId: string) => {
    for (const id of selectedItems) {
      const item = unifiedItems.find(i => i.id === id);
      if (item) moveToSpaceMutation.mutate({ itemId: id, spaceId, itemType: item.itemType });
    }
    setMoveToFolderDialogOpen(false);
    setMoveToFolderSearchQuery("");
    setSelectedItems(new Set());
  };

  const handleMoveToTopicAndClose = (topicId: string) => {
    const topicHomeSpaceId = topics.find((t) => String(t.id) === String(topicId))?.spaceId ?? null;
    for (const id of selectedItems) {
      const item = unifiedItems.find(i => i.id === id);
      if (item)
        moveToTopicMutation.mutate({
          itemId: id,
          topicId,
          itemType: item.itemType,
          topicHomeSpaceId,
        });
    }
    setMoveToFolderDialogOpen(false);
    setMoveToFolderSearchQuery("");
    setSelectedItems(new Set());
  };

  const moveSpacesFiltered = useMemo(() => {
    const q = moveToFolderSearchQuery.trim().toLowerCase();
    if (!q) return spaces;
    return spaces.filter(s => (s.name || "").toLowerCase().includes(q));
  }, [spaces, moveToFolderSearchQuery]);

  const moveTopicsFiltered = useMemo(() => {
    const q = moveToFolderSearchQuery.trim().toLowerCase();
    if (!q) return topics;
    return topics.filter(t => (t.name || "").toLowerCase().includes(q));
  }, [topics, moveToFolderSearchQuery]);

  const handleSort = (field: LibrarySortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const toggleItemSelection = (id: string, shiftKey: boolean = false) => {
    const currentIndex = filteredAndSortedItems.findIndex(item => item.id === id);
    
    let anchorIndex = lastClickedIndexRef.current;
    if (anchorIndex !== null && lastClickedIdRef.current) {
      const anchorCurrentIndex = filteredAndSortedItems.findIndex(item => item.id === lastClickedIdRef.current);
      if (anchorCurrentIndex !== -1) {
        anchorIndex = anchorCurrentIndex;
      } else {
        anchorIndex = null;
      }
    }
    
    if (shiftKey && anchorIndex !== null && currentIndex !== -1) {
      const start = Math.min(anchorIndex, currentIndex);
      const end = Math.max(anchorIndex, currentIndex);
      const rangeIds = filteredAndSortedItems.slice(start, end + 1).map(item => item.id);
      
      setSelectedItems((prev) => {
        const next = new Set(prev);
        rangeIds.forEach(rangeId => next.add(rangeId));
        return next;
      });
    } else {
      setSelectedItems((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    }
    
    if (currentIndex !== -1) {
      lastClickedIndexRef.current = currentIndex;
      lastClickedIdRef.current = id;
    }
  };

  const selectAllItems = () => {
    if (selectedItems.size === filteredAndSortedItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredAndSortedItems.map(item => item.id)));
    }
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;
    if (!confirm(`Are you sure you want to delete ${selectedItems.size} item(s)?`)) return;
    
    for (const id of selectedItems) {
      const item = unifiedItems.find(i => i.id === id);
      if (item) {
        try {
          if (item.itemType === "note") {
            await knowledgeBaseService.deleteKnowledgeBaseItem(id);
          } else {
            await sourcesService.deleteSource(id);
          }
        } catch (error) {
          console.error(`Failed to delete item ${id}:`, error);
        }
      }
    }
    
    queryClient.invalidateQueries({ queryKey: ["/api/knowledge-base"] });
    queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary"] });
    queryClient.invalidateQueries({ queryKey: ["/api/sources"] });
    setSelectedItems(new Set());
    toast({ title: "Items deleted", description: `${selectedItems.size} item(s) have been deleted.` });
  };

  const handleBulkMoveToFolder = async (folderId: string | null) => {
    if (selectedItems.size === 0) return;

    for (const id of selectedItems) {
      const item = unifiedItems.find(i => i.id === id);
      if (item) {
        moveToFolderMutation.mutate({ itemId: id, folderId, itemType: item.itemType });
      }
    }
    
    setSelectedItems(new Set());
  };

  const isLoading = kbLoading || sourcesLoading;

  const renameMutation = useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }) => {
      return knowledgeBaseService.updateKnowledgeBaseItem(id, { title });
    },
    onSuccess: () => {
      toast({ title: "Document renamed", description: "The document title has been updated." });
      queryClient.invalidateQueries({ queryKey: ["/api/knowledge-base"] });
      queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary"] });
      setRenameDialogOpen(false);
      setRenamingItem(null);
      setNewTitle("");
    },
    onError: (error) => {
      toast({ title: "Error renaming", description: error instanceof Error ? error.message : "Failed to rename", variant: "destructive" });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async (id: string) => {
      return knowledgeBaseService.duplicateDocument(id);
    },
    onSuccess: () => {
      toast({ title: "Document duplicated", description: "A copy has been created." });
      queryClient.invalidateQueries({ queryKey: ["/api/knowledge-base"] });
      queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary"] });
    },
    onError: (error) => {
      toast({ title: "Error duplicating", description: error instanceof Error ? error.message : "Failed to duplicate", variant: "destructive" });
    },
  });

  const mergeMutation = useMutation({
    mutationFn: async ({ sourceId, targetId }: { sourceId: string; targetId: string }) => {
      return knowledgeBaseService.mergeDocuments(sourceId, targetId);
    },
    onSuccess: () => {
      toast({ title: "Documents merged", description: "The documents have been combined." });
      queryClient.invalidateQueries({ queryKey: ["/api/knowledge-base"] });
      queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary"] });
      setMergeDialogOpen(false);
      setMergeSourceItem(null);
      setMergeTargetId("");
    },
    onError: (error) => {
      toast({ title: "Error merging", description: error instanceof Error ? error.message : "Failed to merge", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return knowledgeBaseService.deleteKnowledgeBaseItem(id);
    },
    onSuccess: () => {
      toast({ title: "Document deleted", description: "The document has been removed." });
      queryClient.invalidateQueries({ queryKey: ["/api/knowledge-base"] });
      queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary"] });
    },
    onError: (error) => {
      toast({ title: "Error deleting", description: error instanceof Error ? error.message : "Failed to delete", variant: "destructive" });
    },
  });

  const createExpertMutation = useMutation({
    mutationFn: async ({ name, role, bio }: { name: string; role: string; bio: string }) => {
      return sourcesService.createSource({
        title: name,
        content: `**Role:** ${role}\n\n${bio}`,
        type: "sme",
        url: null,
        topics: [],
        subTopics: [],
        tags: ["expert", "sme"],
        linkedThreads: [],
        linkedSpaceIds: [],
        attachments: [],
      } as any);
    },
    onSuccess: () => {
      toast({ title: "Expert added", description: "The subject matter expert has been added to your library." });
      queryClient.invalidateQueries({ queryKey: ["/api/sources"] });
      setExpertDialogOpen(false);
      setNewExpertName("");
      setNewExpertRole("");
      setNewExpertBio("");
    },
    onError: (error) => {
      toast({ title: "Error adding expert", description: error instanceof Error ? error.message : "Failed to add expert", variant: "destructive" });
    },
  });

  const handleAddExpert = () => {
    if (newExpertName.trim() && newExpertRole.trim()) {
      createExpertMutation.mutate({
        name: newExpertName.trim(),
        role: newExpertRole.trim(),
        bio: newExpertBio.trim(),
      });
    }
  };

  const createReferenceMutation = useMutation({
    mutationFn: async ({ url, title }: { url: string; title: string }) => {
      return sourcesService.createSource({
        title: title || url,
        content: "",
        type: "url",
        url: url,
        topics: [],
        subTopics: [],
        tags: [],
        linkedThreads: [],
        linkedSpaceIds: [],
        attachments: [],
      } as any);
    },
    onSuccess: () => {
      toast({ title: "Reference added", description: "The reference has been added to your library." });
      queryClient.invalidateQueries({ queryKey: ["/api/sources"] });
      setAddReferenceDialogOpen(false);
      setNewReferenceUrl("");
      setNewReferenceTitle("");
    },
    onError: (error) => {
      toast({ title: "Error adding reference", description: error instanceof Error ? error.message : "Failed to add reference", variant: "destructive" });
    },
  });

  const handleAddReference = () => {
    if (newReferenceUrl.trim()) {
      createReferenceMutation.mutate({
        url: newReferenceUrl.trim(),
        title: newReferenceTitle.trim() || newReferenceUrl.trim(),
      });
    }
  };

  const handleManageChildDocs = (item: UnifiedLibraryItem) => {
    setSelectedExpertItem(item);
    setChildDocsDialogOpen(true);
  };

  const getChildDocs = (expertId: string): UnifiedLibraryItem[] => {
    return unifiedItems.filter(item => {
      const tags = Array.isArray(item.tags) ? item.tags : [];
      return tags.includes(`parent:${expertId}`);
    });
  };

  // Handler for DocumentItem's onDocumentClick (handles locked documents)
  const recordDocumentOpen = useCallback(
    (ref: LibraryDocRef) => {
      touchRecent(ref);
    },
    [touchRecent]
  );

  const handleDocumentClick = useCallback(
    (documentId: string, isLocked?: boolean) => {
      if (isLocked && !pinStatus?.isSessionValid) {
        setPendingDocumentIdToOpen(documentId);
        setShowPinDialogForDocument(true);
        return;
      }
      const item = unifiedItems.find((i) => i.id === documentId);
      if (item) {
        recordDocumentOpen({ id: item.id, itemType: item.itemType });
      }
      setIsCollapsed(false);
      setSelectedReferenceId(null);
      setSelectedDocumentId(documentId);
    },
    [
      pinStatus?.isSessionValid,
      setIsCollapsed,
      setSelectedDocumentId,
      setSelectedReferenceId,
      unifiedItems,
      recordDocumentOpen,
    ]
  );

  // Refresh handler for DocumentItem
  const handleRefreshDocuments = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["/api/knowledge-base"] });
    queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary"] });
  }, [queryClient]);

  const handleToggleFavorite = useCallback(
    async (item: UnifiedLibraryItem) => {
      const ref: LibraryDocRef = { id: item.id, itemType: item.itemType };
      const wasFavorite = isFavorite(item.id, item.itemType);
      try {
        await toggleFavorite(ref);
        toast({
          title: wasFavorite ? "Removed from favorites" : "Added to favorites",
        });
      } catch {
        toast({
          title: "Could not update favorites",
          variant: "destructive",
        });
      }
    },
    [isFavorite, toggleFavorite, toast]
  );

  const renderFavoriteMenuItem = (
    item: UnifiedLibraryItem,
    stopPropagation?: boolean
  ) => (
    <DropdownMenuItem
      onClick={(e) => {
        if (stopPropagation) e.stopPropagation();
        handleToggleFavorite(item);
      }}
    >
      <Star
        className={cn(
          "h-4 w-4 mr-2",
          isFavorite(item.id, item.itemType) && "fill-amber-500 text-amber-500"
        )}
      />
      {isFavorite(item.id, item.itemType)
        ? "Remove from favorites"
        : "Add to favorites"}
    </DropdownMenuItem>
  );

  const handleOpenDocument = (item: UnifiedLibraryItem) => {
    recordDocumentOpen({ id: item.id, itemType: item.itemType });
    if (item.itemType === "note") {
      if (item.isLocked && !pinStatus?.isSessionValid) {
        setPendingDocumentIdToOpen(item.id);
        setShowPinDialogForDocument(true);
        return;
      }
      setIsCollapsed(false);
      setSelectedReferenceId(null);
      setSelectedDocumentId(item.id);
    } else if (item.itemType === "reference") {
      // Open Ref in right sidebar for editing (same panel as Notes)
      setIsCollapsed(false);
      setSelectedDocumentId(null);
      setSelectedReferenceId(item.id);
    } else if (item.url) {
      // Fallback for items with url that are not typed as reference
      const filename = item.title || item.url;
      const ext = filename.toLowerCase().split(".").pop() || "";
      const previewableExts = ["pdf", "jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "mp4", "webm", "mov", "doc", "docx", "xls", "xlsx", "ppt", "pptx"];
      if (previewableExts.includes(ext) || item.type?.toLowerCase().includes("pdf") || item.type?.toLowerCase().includes("document")) {
        setPreviewFile({
          id: item.id,
          filename: item.title || "Document",
          url: item.url,
        });
        setFilePreviewOpen(true);
      } else {
        window.open(item.url || undefined, "_blank");
      }
    }
  };

  const handleRename = (item: UnifiedLibraryItem) => {
    setRenamingItem(item);
    setNewTitle(item.title);
    setRenameDialogOpen(true);
  };

  const handleConfirmRename = () => {
    if (renamingItem && newTitle.trim()) {
      renameMutation.mutate({ id: renamingItem.id, title: newTitle.trim() });
    }
  };

  const handleDuplicate = (item: UnifiedLibraryItem) => {
    if (item.itemType === "note") {
      duplicateMutation.mutate(item.id);
    }
  };

  const handleMerge = (item: UnifiedLibraryItem) => {
    setMergeSourceItem(item);
    setMergeTargetId("");
    setMergeDialogOpen(true);
  };

  const handleConfirmMerge = () => {
    if (mergeSourceItem && mergeTargetId) {
      mergeMutation.mutate({ sourceId: mergeSourceItem.id, targetId: mergeTargetId });
    }
  };

  const handleDelete = (item: UnifiedLibraryItem) => {
    if (confirm(`Are you sure you want to delete "${item.title}"?`)) {
      if (item.itemType === "note") {
        deleteMutation.mutate(item.id);
      }
    }
  };

  const handleEmbedForSearch = async (item: UnifiedLibraryItem) => {
    if (item.itemType !== "note") return;
    try {
      await ragService.embedDocument(item.id);
      toast({ title: "Embedded for search", description: `"${item.title}" is now searchable by meaning.` });
      queryClient.invalidateQueries({ queryKey: ["embedding-status"] });
    } catch (e) {
      toast({ title: "Embed failed", description: e instanceof Error ? e.message : "Could not embed document.", variant: "destructive" });
    }
  };

  const handleReembedForSearch = async (item: UnifiedLibraryItem) => {
    if (item.itemType !== "note") return;
    try {
      await ragService.reembedDocument(item.id);
      toast({ title: "Re-embedded for search", description: `"${item.title}" has been updated for semantic search.` });
      queryClient.invalidateQueries({ queryKey: ["embedding-status"] });
    } catch (e) {
      toast({ title: "Re-embed failed", description: e instanceof Error ? e.message : "Could not re-embed document.", variant: "destructive" });
    }
  };

  const handleRemoveFromSearch = async (item: UnifiedLibraryItem) => {
    if (item.itemType !== "note") return;
    try {
      await ragService.deleteDocumentEmbeddings(item.id);
      toast({ title: "Removed from search", description: `"${item.title}" is no longer embedded.` });
      queryClient.invalidateQueries({ queryKey: ["embedding-status"] });
    } catch (e) {
      toast({ title: "Remove failed", description: e instanceof Error ? e.message : "Could not remove embeddings.", variant: "destructive" });
    }
  };

  const handleToggleIncludeInRag = async (item: UnifiedLibraryItem, value: boolean) => {
    if (item.itemType !== "note") return;
    try {
      await knowledgeBaseService.updateKnowledgeBaseItem(item.id, { includeInRag: value });
      toast({ title: value ? "Included in RAG" : "Excluded from RAG", description: `"${item.title}" is ${value ? "in" : "out of"} RAG retrieval.` });
      queryClient.invalidateQueries({ queryKey: ["/api/knowledge-base"] });
    } catch (e) {
      toast({ title: "Update failed", description: e instanceof Error ? e.message : "Could not update.", variant: "destructive" });
    }
  };

  const handleOpenInspector = (item: UnifiedLibraryItem) => {
    setInspectorItem(item);
    setInspectorOpen(true);
  };

  const moveToFolderMutation = useMutation({
    mutationFn: async ({ itemId, folderId, itemType }: { itemId: string; folderId: string | null; itemType: "note" | "reference" }) => {
      await moveLibraryDocumentToFolder(queryClient, itemId, folderId, itemType);
    },
    onSuccess: async (_data, variables) => {
      toast({ title: "Item moved", description: "The item has been moved to the folder." });
      setOptimisticallyMovedIds((prev) => {
        const next = new Set(prev);
        next.delete(variables.itemId);
        return next;
      });
    },
    onError: (error, variables) => {
      setOptimisticallyMovedIds((prev) => {
        const next = new Set(prev);
        next.delete(variables.itemId);
        return next;
      });
      toast({ title: "Error moving item", description: error instanceof Error ? error.message : "Failed to move", variant: "destructive" });
    },
  });

  const handleMoveToFolder = (item: UnifiedLibraryItem, folderId: string | null) => {
    moveToFolderMutation.mutate({ itemId: item.id, folderId, itemType: item.itemType });
  };

  const libraryDocumentActions = useMemo((): LibraryDocumentActions => {
    const resolve = (item: DocumentActionsItem) => unifiedItemsById.get(item.id);

    return {
      onEdit: (item) => {
        if (item.itemType === "note") {
          handleDocumentClick(item.id, item.isLocked);
        }
      },
      onRename: (item) => {
        const u = resolve(item);
        if (u) handleRename(u);
      },
      onDuplicate: (item) => {
        const u = resolve(item);
        if (u) handleDuplicate(u);
      },
      onMerge: (item) => {
        const u = resolve(item);
        if (u) handleMerge(u);
      },
      onManageLinks: (item) => {
        const u = resolve(item);
        if (u) handleOpenInspector(u);
      },
      onReembedForSearch: async (item) => {
        const u = resolve(item);
        if (u) await handleReembedForSearch(u);
      },
      onEmbedForSearch: async (item) => {
        const u = resolve(item);
        if (u) await handleEmbedForSearch(u);
      },
      onRemoveFromSearch: async (item) => {
        const u = resolve(item);
        if (u) await handleRemoveFromSearch(u);
      },
      onToggleIncludeInRag: async (item, value) => {
        const u = resolve(item);
        if (u) await handleToggleIncludeInRag(u, value);
      },
      onMoveToFolder: (item) => {
        setSelectedItems(new Set([item.id]));
        setMoveToFolderSearchQuery("");
        setMoveToFolderDialogOpen(true);
      },
      onRemoveFromFolder: (item) => {
        const u = resolve(item);
        if (u) handleMoveToFolder(u, null);
      },
      onDelete: (item) => {
        const u = resolve(item);
        if (u) handleDelete(u);
      },
      onOpenLink: (item) => {
        if (item.url) window.open(item.url, "_blank");
      },
      onViewWorks: (item) => {
        const u = resolve(item);
        if (u) handleManageChildDocs(u);
      },
      onToggleSourceOfTruth: async (item) => {
        try {
          const added = await toggleDocumentSourceOfTruth(item.id);
          toast({
            title: added ? "Source of Truth" : "Removed from canon",
            description: added
              ? `"${item.title ?? "Document"}" is always included in chat context.`
              : `"${item.title ?? "Document"}" is no longer in your global canon.`,
          });
        } catch (e) {
          toast({
            title: "Could not update",
            description: e instanceof Error ? e.message : "Unknown error",
            variant: "destructive",
          });
        }
      },
      onCopyPublicLink: (item) => {
        if (item.itemType === "note") void copyPublicLink(item.id);
      },
      showCopyPublicLink: true,
    };
  }, [
    unifiedItemsById,
    handleDocumentClick,
    toast,
    copyPublicLink,
    handleRename,
    handleDuplicate,
    handleMerge,
    handleOpenInspector,
    handleReembedForSearch,
    handleEmbedForSearch,
    handleRemoveFromSearch,
    handleToggleIncludeInRag,
    handleMoveToFolder,
    handleDelete,
    handleManageChildDocs,
  ]);

  const renderBrowseListRow = useCallback(
    (item: UnifiedLibraryItem) => {
      const isSelected = selectedItems.has(item.id);
      const actionsItem = unifiedItemToDocumentActions(item);
      return (
        <DraggableLibraryItem item={item}>
          <div
            className={cn(
              "flex items-center gap-2 group min-w-0 w-full",
              isSelected && "ring-2 ring-primary rounded-md"
            )}
          >
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => toggleItemSelection(item.id, false)}
              onPointerDown={(e) => {
                if (e.shiftKey) {
                  e.preventDefault();
                  toggleItemSelection(item.id, true);
                }
              }}
              onClick={(e) => e.stopPropagation()}
              className="shrink-0"
              data-testid={`checkbox-list-item-${item.id}`}
            />
            <div className="flex-1 min-w-0">
              <LibraryBrowseDocumentRow
                item={actionsItem}
                viewMode="list"
                typeIcon={getTypeIcon(item.type, item.itemType, item.tags, item.url)}
                typeLabel={getTypeLabel(item.type, item.itemType, item.tags, item.url)}
                updatedAt={item.updatedAt}
                embeddingStatusMap={embeddingStatusMap}
                documentActions={libraryDocumentActions}
                onOpen={() => handleOpenDocument(item)}
                isFavorite={isFavorite(item.id, item.itemType)}
                onToggleFavorite={() => handleToggleFavorite(item)}
                isSourceOfTruth={sourceOfTruthIdSet.has(item.id)}
              />
            </div>
          </div>
        </DraggableLibraryItem>
      );
    },
    [
      selectedItems,
      embeddingStatusMap,
      libraryDocumentActions,
      handleOpenDocument,
      handleDocumentClick,
      isFavorite,
      handleToggleFavorite,
      toggleItemSelection,
      sourceOfTruthIdSet,
    ]
  );

  const moveToSpaceMutation = useMutation({
    mutationFn: async ({ itemId, spaceId, itemType }: { itemId: string; spaceId: string; itemType: "note" | "reference" }) => {
      await linkLibraryDocumentToSpace(queryClient, itemId, itemType, spaceId);
    },
    onSuccess: async (_data, variables) => {
      toast({ title: "Item moved", description: "The item has been moved to the space." });
      setOptimisticallyMovedIds((prev) => {
        const next = new Set(prev);
        next.delete(variables.itemId);
        return next;
      });
    },
    onError: (error, variables) => {
      setOptimisticallyMovedIds((prev) => {
        const next = new Set(prev);
        next.delete(variables.itemId);
        return next;
      });
      toast({ title: "Error moving item", description: error instanceof Error ? error.message : "Failed to move", variant: "destructive" });
    },
  });

  const moveToTopicMutation = useMutation({
    mutationFn: async ({
      itemId,
      topicId,
      itemType,
      topicHomeSpaceId,
    }: {
      itemId: string;
      topicId: string;
      itemType: "note" | "reference";
      topicHomeSpaceId?: string | null;
    }) => {
      await linkLibraryDocumentToTopic(queryClient, itemId, itemType, topicId, topicHomeSpaceId ?? undefined);
    },
    onSuccess: () => {
      toast({ title: "Item moved", description: "The item has been moved to the topic." });
    },
    onError: (error) => {
      toast({ title: "Error moving item", description: error instanceof Error ? error.message : "Failed to move", variant: "destructive" });
    },
  });

  const effectiveNewFolderParentId = newFolderDialogParentId ?? (
    selectedFolderId && selectedFolderId !== "spaces" && !selectedFolderId.startsWith("space-") ? selectedFolderId : null
  );

  const createFolderMutation = useMutation({
    mutationFn: async ({ name, parentId }: { name: string; parentId: string | null }) => {
      return foldersService.createFolder({ name, parentId });
    },
    onSuccess: (_, variables) => {
      toast({ title: "Folder created", description: variables.parentId ? "Subfolder created." : "Folder created." });
      queryClient.invalidateQueries({ queryKey: ["/api/library-folders"] });
      setNewFolderDialogOpen(false);
      setNewFolderName("");
      setNewFolderDialogParentId(null);
    },
    onError: (error) => {
      toast({
        title: "Error creating folder",
        description: error instanceof Error ? error.message : "Failed to create folder",
        variant: "destructive",
      });
    },
  });

  const openNewFolderDialog = (parentId: string | null) => {
    setNewFolderDialogParentId(parentId);
    setNewFolderName("");
    setNewFolderDialogOpen(true);
  };

  const handleCreateFolderFromLibrary = () => {
    if (newFolderName.trim()) {
      createFolderMutation.mutate({ name: newFolderName.trim(), parentId: effectiveNewFolderParentId });
    }
  };

  const updateFolderMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      return foldersService.updateFolder(id, { name });
    },
    onSuccess: () => {
      toast({ title: "Folder renamed" });
      queryClient.invalidateQueries({ queryKey: ["/api/library-folders"] });
      setEditFolderDialogOpen(false);
      setEditingFolder(null);
      setEditFolderName("");
    },
    onError: (error) => {
      toast({
        title: "Error renaming folder",
        description: error instanceof Error ? error.message : "Failed to rename folder",
        variant: "destructive",
      });
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: async (id: string) => foldersService.deleteFolder(id),
    onSuccess: (_, deletedId) => {
      toast({ title: "Folder deleted" });
      queryClient.invalidateQueries({ queryKey: ["/api/library-folders"] });
      if (selectedFolderId === deletedId) setSelectedFolderId(null);
    },
    onError: (error) => {
      toast({
        title: "Error deleting folder",
        description: error instanceof Error ? error.message : "Failed to delete folder",
        variant: "destructive",
      });
    },
  });

  const openEditFolderDialog = (folder: LibraryFolder) => {
    setEditingFolder(folder);
    setEditFolderName(folder.name);
    setEditFolderDialogOpen(true);
  };

  const handleUpdateFolderFromLibrary = () => {
    if (editingFolder && editFolderName.trim()) {
      updateFolderMutation.mutate({ id: editingFolder.id, name: editFolderName.trim() });
    }
  };

  const handleDeleteFolderFromLibrary = (folder: LibraryFolder) => {
    if (confirm(`Are you sure you want to delete "${folder.name}"?`)) {
      deleteFolderMutation.mutate(folder.id);
    }
  };

  const noteItems = useMemo(() => {
    return unifiedItems.filter(item => item.itemType === "note" && item.id !== mergeSourceItem?.id);
  }, [unifiedItems, mergeSourceItem]);

  const filteredBlueprints = useMemo(() => {
    let filtered = [...ALL_BLUEPRINTS];
    
    if (blueprintFilter === "edition") {
      filtered = [...EDITION_BLUEPRINTS];
    } else if (blueprintFilter === "research") {
      filtered = [...RESEARCH_BLUEPRINTS];
    }
    
    if (blueprintSearch.trim()) {
      const query = blueprintSearch.toLowerCase();
      filtered = filtered.filter(
        (bp) =>
          bp.name.toLowerCase().includes(query) ||
          bp.description.toLowerCase().includes(query) ||
          bp.category.toLowerCase().includes(query) ||
          bp.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    }
    
    filtered.sort((a, b) => {
      const dir = blueprintSortDirection === "asc" ? 1 : -1;
      if (blueprintSortField === "name") {
        return dir * a.name.localeCompare(b.name);
      } else if (blueprintSortField === "category") {
        return dir * a.category.localeCompare(b.category);
      } else if (blueprintSortField === "type") {
        return dir * a.type.localeCompare(b.type);
      }
      return 0;
    });
    
    return filtered;
  }, [blueprintFilter, blueprintSearch, blueprintSortField, blueprintSortDirection]);

  const getBlueprintIcon = (iconName: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      GraduationCap: <GraduationCap className="h-5 w-5" />,
      ChefHat: <ChefHat className="h-5 w-5" />,
      Briefcase: <Briefcase className="h-5 w-5" />,
      Palette: <Palette className="h-5 w-5" />,
      Heart: <Heart className="h-5 w-5" />,
      Wallet: <Wallet className="h-5 w-5" />,
      BookOpen: <BookOpen className="h-5 w-5" />,
      TrendingUp: <TrendingUp className="h-5 w-5" />,
      Compass: <Compass className="h-5 w-5" />,
      GitCompare: <GitCompare className="h-5 w-5" />,
      Lightbulb: <Lightbulb className="h-5 w-5" />,
      Target: <Target className="h-5 w-5" />,
      FileText: <FileText className="h-5 w-5" />,
      Users: <Users className="h-5 w-5" />,
    };
    return iconMap[iconName] || <Sparkles className="h-5 w-5" />;
  };

  const handleCreateFromBlueprint = (blueprint: Blueprint) => {
    if (blueprint.type === "edition") {
      navigate(`/spaces/new?blueprint=${blueprint.id}`);
    } else {
      navigate(`/chat?template=${blueprint.id}`);
    }
  };

  const renderMetadataPills = (item: UnifiedLibraryItem, compact: boolean = false) => {
    const linkedSpaceIds = Array.isArray(item.linkedSpaceIds) ? item.linkedSpaceIds : [];
    const linkedTopicIds = Array.isArray(item.linkedTopicIds) ? item.linkedTopicIds : [];
    const linkedThreadIds = Array.isArray(item.linkedThreadIds) ? item.linkedThreadIds : [];
    const tags = Array.isArray(item.tags) ? item.tags : [];
    
    const hasSpaces = linkedSpaceIds.length > 0;
    const hasTopics = linkedTopicIds.length > 0;
    const hasThreads = linkedThreadIds.length > 0;
    const hasTags = tags.length > 0;
    const hasAny = hasSpaces || hasTopics || hasThreads || hasTags;

    if (!hasAny) {
      return (
        <span className="text-xs text-muted-foreground italic">No links</span>
      );
    }

    if (compact) {
      return (
        <div className="flex items-center gap-1.5 flex-wrap">
          {hasSpaces && (
            <Badge variant="outline" className="text-xs gap-1 bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400">
              <Layers className="h-3 w-3" />
              {linkedSpaceIds.length}
            </Badge>
          )}
          {hasTopics && (
            <Badge variant="outline" className="text-xs gap-1 bg-purple-500/10 border-purple-500/30 text-purple-600 dark:text-purple-400">
              <Folder className="h-3 w-3" />
              {linkedTopicIds.length}
            </Badge>
          )}
          {hasThreads && (
            <Badge variant="outline" className="text-xs gap-1 bg-green-500/10 border-green-500/30 text-green-600 dark:text-green-400">
              <MessageCircle className="h-3 w-3" />
              {linkedThreadIds.length}
            </Badge>
          )}
          {hasTags && (
            <Badge variant="outline" className="text-xs gap-1 bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400">
              <Tag className="h-3 w-3" />
              {tags.length}
            </Badge>
          )}
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-1.5">
        {linkedSpaceIds.slice(0, 2).map((spaceId) => {
          const space = spacesMap.get(spaceId);
          return (
            <Badge 
              key={spaceId} 
              variant="outline" 
              className="text-xs gap-1 cursor-pointer hover:bg-blue-500/20 bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400"
              onClick={(e) => { e.stopPropagation(); navigate(`/spaces/${spaceId}`); }}
            >
              <div 
                className="w-2 h-2 rounded-full" 
                style={{ backgroundColor: space?.color || "#3b82f6" }} 
              />
              {space?.name || "Space"}
            </Badge>
          );
        })}
        {linkedSpaceIds.length > 2 && (
          <Badge variant="outline" className="text-xs bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400">
            +{linkedSpaceIds.length - 2}
          </Badge>
        )}
        
        {linkedTopicIds.slice(0, 2).map((topicId) => {
          const topic = topicsMap.get(topicId);
          return (
            <Badge 
              key={topicId} 
              variant="outline" 
              className="text-xs gap-1 cursor-pointer hover:bg-purple-500/20 bg-purple-500/10 border-purple-500/30 text-purple-600 dark:text-purple-400"
              onClick={(e) => { e.stopPropagation(); navigate(`/topics`); }}
            >
              <Folder className="h-3 w-3 text-purple-500 dark:text-purple-400" />
              {topic?.name || "Topic"}
            </Badge>
          );
        })}
        {linkedTopicIds.length > 2 && (
          <Badge variant="outline" className="text-xs bg-purple-500/10 border-purple-500/30 text-purple-600 dark:text-purple-400">
            +{linkedTopicIds.length - 2}
          </Badge>
        )}

        {linkedThreadIds.slice(0, 1).map((threadId) => {
          const thread = threadsMap.get(threadId);
          return (
            <Badge 
              key={threadId} 
              variant="outline" 
              className="text-xs gap-1 cursor-pointer hover:bg-green-500/20 bg-green-500/10 border-green-500/30 text-green-600 dark:text-green-400"
              onClick={(e) => { e.stopPropagation(); navigate(`/chat?threadId=${threadId}`); }}
            >
              <MessageCircle className="h-3 w-3 text-green-500 dark:text-green-400" />
              {thread?.title?.slice(0, 15) || "Thread"}...
            </Badge>
          );
        })}
        {linkedThreadIds.length > 1 && (
          <Badge variant="outline" className="text-xs bg-green-500/10 border-green-500/30 text-green-600 dark:text-green-400">
            +{linkedThreadIds.length - 1}
          </Badge>
        )}

        {tags.slice(0, 2).map((tagId) => {
          const tag = tagsMap.get(tagId);
          return (
            <Badge 
              key={tagId} 
              variant="outline" 
              className="text-xs gap-1 bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400"
            >
              <Tag className="h-3 w-3 text-amber-500 dark:text-amber-400" />
              {tag?.name || tagId}
            </Badge>
          );
        })}
        {tags.length > 2 && (
          <Badge variant="outline" className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400">
            +{tags.length - 2}
          </Badge>
        )}
      </div>
    );
  };

  const renderLinksSection = (item: UnifiedLibraryItem) => {
    const linkedSpaceIds = Array.isArray(item.linkedSpaceIds) ? item.linkedSpaceIds : [];
    const linkedTopicIds = Array.isArray(item.linkedTopicIds) ? item.linkedTopicIds : [];
    const linkedThreadIds = Array.isArray(item.linkedThreadIds) ? item.linkedThreadIds : [];
    const itemTags = Array.isArray(item.tags) ? item.tags : [];
    const totalLinks = linkedSpaceIds.length + linkedTopicIds.length + linkedThreadIds.length + itemTags.length;
    const isExpanded = expandedItems.has(item.id);

    if (totalLinks === 0) {
      return (
        <span className="text-xs text-muted-foreground italic">Not linked</span>
      );
    }

    return (
      <Collapsible open={isExpanded} onOpenChange={() => toggleExpanded(item.id)}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="h-auto py-1 px-2 text-xs gap-1 flex-wrap justify-start">
            {renderMetadataPills(item, true)}
            {isExpanded ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <div className="space-y-2 pl-2 border-l-2 border-muted">
            {linkedSpaceIds.length > 0 && (
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                  <Layers className="h-3 w-3" /> Spaces
                </div>
                <div className="flex flex-wrap gap-1">
                  {linkedSpaceIds.map((spaceId) => {
                    const space = spacesMap.get(spaceId);
                    return (
                      <Badge 
                        key={spaceId} 
                        variant="outline" 
                        className="text-xs gap-1 cursor-pointer hover:bg-blue-100"
                        onClick={() => navigate(`/spaces/${spaceId}`)}
                      >
                        <div 
                          className="w-2 h-2 rounded-full" 
                          style={{ backgroundColor: space?.color || "#888" }} 
                        />
                        {space?.name || "Unknown"}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
            
            {linkedTopicIds.length > 0 && (
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                  <Folder className="h-3 w-3" /> Topics
                </div>
                <div className="flex flex-wrap gap-1">
                  {linkedTopicIds.map((topicId) => {
                    const topic = topicsMap.get(topicId);
                    return (
                      <Badge 
                        key={topicId} 
                        variant="outline" 
                        className="text-xs gap-1 cursor-pointer hover:bg-purple-100"
                        onClick={() => navigate(`/topics`)}
                      >
                        {topic?.name || "Unknown Topic"}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
            
            {linkedThreadIds.length > 0 && (
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                  <MessageCircle className="h-3 w-3" /> Threads
                </div>
                <div className="flex flex-wrap gap-1">
                  {linkedThreadIds.map((threadId) => {
                    const thread = threadsMap.get(threadId);
                    return (
                      <Badge 
                        key={threadId} 
                        variant="outline" 
                        className="text-xs gap-1 cursor-pointer hover:bg-green-100"
                        onClick={() => navigate(`/chat?threadId=${threadId}`)}
                      >
                        {thread?.title || "Untitled Thread"}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
            
            {itemTags.length > 0 && (
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                  <Tag className="h-3 w-3" /> Tags
                </div>
                <div className="flex flex-wrap gap-1">
                  {itemTags.map((tagId) => {
                    const tag = tagsMap.get(tagId);
                    return (
                      <Badge 
                        key={tagId} 
                        variant="outline" 
                        className="text-xs"
                      >
                        {tag?.name || tagId}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  };

  const renderTutorialGridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {filteredTutorialVideos.map((v) => (
        <Card key={v.id} className="overflow-hidden flex flex-col">
          <TutorialPlayPreview
            video={v}
            onOpen={setTutorialPlaybackVideo}
            className="rounded-none"
          />
          <CardContent className="p-4 flex-1 flex flex-col">
            <div className="flex items-start gap-2">
              <Video className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <h3 className="font-medium text-sm line-clamp-2">{v.title}</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Updated {formatDistanceToNow(new Date(v.updatedAt), { addSuffix: true })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderTutorialListView = () => (
    <div className="space-y-0.5 overflow-x-hidden w-full">
      {filteredTutorialVideos.map((v) => (
        <Card key={v.id} className="overflow-hidden">
          <CardContent className="py-2 px-2.5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <TutorialPlayPreview
                video={v}
                onOpen={setTutorialPlaybackVideo}
                className="sm:w-56 sm:shrink-0 rounded-md"
              />
              <div className="flex-1 min-w-0 flex items-start gap-2">
                <Video className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-sm">{v.title}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Tutorial · Updated {formatDistanceToNow(new Date(v.updatedAt), { addSuffix: true })}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderTutorialTableView = () => (
    <div className="border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-14" />
              <TableHead>Title</TableHead>
              <TableHead className="w-[120px]">Type</TableHead>
              <TableHead className="w-[160px]">Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredTutorialVideos.map((v) => (
              <TableRow key={v.id}>
                <TableCell className="w-14 py-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 shrink-0"
                    onClick={() => setTutorialPlaybackVideo(v)}
                    aria-label={`Play ${v.title}`}
                  >
                    <Play className="h-5 w-5 fill-primary text-primary" />
                  </Button>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2 min-w-0">
                    <Video className="h-4 w-4 text-primary shrink-0" />
                    <span className="font-medium truncate">{v.title}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary" className="text-xs">
                    Tutorial
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {formatDistanceToNow(new Date(v.updatedAt), { addSuffix: true })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const renderGridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {browseDocumentItems.map((item) => {
        const isSelected = selectedItems.has(item.id);
        
        // Use DocumentItem for notes, custom rendering for references
        if (item.itemType === "note") {
          const actionsItem = unifiedItemToDocumentActions(item);
          const folderName = item.folderId
            ? foldersById.get(item.folderId)
            : null;
          return (
            <DraggableLibraryItem key={item.id} item={item}>
              <div className={`relative group ${isSelected ? "ring-2 ring-primary rounded-md" : ""}`}>
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleItemSelection(item.id, false)}
                  onPointerDown={(e) => {
                    if (e.shiftKey) {
                      e.preventDefault();
                      toggleItemSelection(item.id, true);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="absolute top-2 left-2 z-10 bg-background/80 backdrop-blur-sm"
                  data-testid={`checkbox-item-${item.id}`}
                />
                <LibraryBrowseDocumentRow
                  item={actionsItem}
                  viewMode="grid"
                  typeIcon={getTypeIcon(item.type, item.itemType, item.tags, item.url)}
                  typeLabel={getTypeLabel(item.type, item.itemType, item.tags, item.url)}
                  folderName={folderName}
                  updatedAt={item.updatedAt}
                  embeddingStatusMap={embeddingStatusMap}
                  documentActions={libraryDocumentActions}
                  onOpen={() => handleOpenDocument(item)}
                  isFavorite={isFavorite(item.id, item.itemType)}
                  onToggleFavorite={() => handleToggleFavorite(item)}
                  isSourceOfTruth={sourceOfTruthIdSet.has(item.id)}
                />
              </div>
            </DraggableLibraryItem>
          );
        }
        
        // Custom rendering for references
        return (
          <DraggableLibraryItem key={item.id} item={item}>
          <Card
            className={`group hover:shadow-md transition-shadow cursor-pointer ${isSelected ? 'ring-2 ring-primary border-primary' : ''}`}
            onClick={() => handleOpenDocument(item)}
          >
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleItemSelection(item.id, false)}
                  onPointerDown={(e) => {
                    if (e.shiftKey) {
                      e.preventDefault();
                      toggleItemSelection(item.id, true);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="mr-1"
                  data-testid={`checkbox-item-${item.id}`}
                />
                {getTypeIcon(item.type, item.itemType, item.tags, item.url)}
                <Badge variant="secondary" className="text-xs">
                  {getTypeLabel(item.type, item.itemType, item.tags, item.url)}
                </Badge>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {item.url && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); window.open(item.url || undefined, "_blank"); }}>
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open Link
                    </DropdownMenuItem>
                  )}
                  {isExpertItem(item.type, item.tags) && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleManageChildDocs(item); }}>
                      <BookMarked className="h-4 w-4 mr-2" />
                      View Works
                    </DropdownMenuItem>
                  )}
                  {renderFavoriteMenuItem(item, true)}
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenInspector(item); }}>
                    <Link2 className="h-4 w-4 mr-2" />
                    Manage Links
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                    <EntityLockButton
                      entityType="document"
                      entityId={item.id}
                      isLocked={item.isLocked === true}
                      size="menu"
                      customTitle={item.isLocked ? "Unlock document" : "Lock document"}
                    />
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenInspector(item); }}>
                    <Folder className="h-4 w-4 mr-2" />
                    Move to Folder...
                  </DropdownMenuItem>
                  {item.folderId && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleMoveToFolder(item, null); }}>
                      <X className="h-4 w-4 mr-2" />
                      Remove from Folder
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {item.folderId && (
              <div className="mb-2">
                <Badge variant="outline" className="text-xs gap-1 bg-amber-50 border-amber-200 text-amber-700">
                  <Folder className="h-3 w-3" />
                  {folders.find(f => f.id === item.folderId)?.name || "Folder"}
                </Badge>
              </div>
            )}

            <h3 
              className="font-medium text-sm line-clamp-2 mb-2 hover:text-primary transition-colors"
              onClick={(e) => { e.stopPropagation(); handleOpenDocument(item); }}
            >
              {item.title}
            </h3>

            {item.url && (
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline flex items-center gap-1 mb-2"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-3 w-3" />
                Open link
              </a>
            )}

            {!isCompactBrowseUi && (
              <div className="mb-2" onClick={(e) => e.stopPropagation()}>
                {renderLinksSection(item)}
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Updated {formatDistanceToNow(new Date(item.updatedAt), { addSuffix: true })}
            </p>
            </CardContent>
          </Card>
          </DraggableLibraryItem>
        );
      })}
      </div>
    );

    // State to track library main section width for responsive title truncation (consistent with Spaces page)
    const [libraryMainSectionWidth, setLibraryMainSectionWidth] = useState(0);
    const libraryMainSectionRef = useRef<HTMLDivElement>(null);

    // Measure the library main section width using ResizeObserver so title length responds to sidebar resize
    useEffect(() => {
      const container = libraryMainSectionRef.current;
      if (!container) return;

      const updateWidth = () => {
        const width = container.offsetWidth;
        if (width > 0) {
          setLibraryMainSectionWidth(width);
        }
      };

      // Initial measurement
      const timeoutId = setTimeout(updateWidth, 100);
      requestAnimationFrame(updateWidth);

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          if (width > 0) {
            setLibraryMainSectionWidth(width);
          }
        }
      });

      resizeObserver.observe(container);
      window.addEventListener('resize', updateWidth);

      return () => {
        clearTimeout(timeoutId);
        resizeObserver.disconnect();
        window.removeEventListener('resize', updateWidth);
      };
    }, [librarySection]);

    // Helper function to truncate title for list view based on main section width
    const truncateTitle = (title: string): string => {
      if (!title) return title;

      if (libraryMainSectionWidth === 0) {
        return title.length > 30 ? title.substring(0, 30).trim() + '...' : title;
      }

      // Account for fixed-width elements: checkbox (16px) + icon/badge (~60px) + metadata pills (~80px) + timestamp (~100px) + menu (28px) + gaps (~40px) = ~324px
      const availableWidth = libraryMainSectionWidth - 324;
      const avgCharWidth = 8;
      const maxLength = Math.max(10, Math.floor(availableWidth / avgCharWidth * 0.9));

      if (title.length <= maxLength) return title;
      return title.substring(0, maxLength).trim() + '...';
    };

    // Helper function to truncate title for table view based on main section width
    const truncateTableTitle = (title: string): string => {
      if (!title) return title;

      if (libraryMainSectionWidth === 0) {
        return title.length > 40 ? title.substring(0, 40).trim() + '...' : title;
      }

      // Account for fixed-width columns: Type (100px) + Folder (140px) + Links (100px) + Updated (120px) + Menu (40px) + gaps (~40px) = ~540px; small screens ~140px
      const isSmallScreen = libraryMainSectionWidth < 1024;
      const fixedWidth = isSmallScreen ? 140 : 540;
      const availableWidth = libraryMainSectionWidth - fixedWidth;
      const avgCharWidth = 8;
      const maxLength = Math.max(15, Math.floor(availableWidth / avgCharWidth * 0.9));

      if (title.length <= maxLength) return title;
      return title.substring(0, maxLength).trim() + '...';
    };

    const renderListView = () => (
          <div className="space-y-0.5 overflow-x-hidden w-full">
        {browseDocumentItems.map((item) => {
          const isSelected = selectedItems.has(item.id);
          
          // Use DocumentItem for notes, custom rendering for references
          if (item.itemType === "note") {
            const actionsItem = unifiedItemToDocumentActions(item);
            return (
              <DraggableLibraryItem key={item.id} item={item}>
                <div className={`flex items-center gap-2 group ${isSelected ? "ring-2 ring-primary rounded-md" : ""}`}>
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleItemSelection(item.id, false)}
                    onPointerDown={(e) => {
                      if (e.shiftKey) {
                        e.preventDefault();
                        toggleItemSelection(item.id, true);
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="shrink-0"
                    data-testid={`checkbox-list-item-${item.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <LibraryBrowseDocumentRow
                      item={actionsItem}
                      viewMode="list"
                      typeIcon={getTypeIcon(item.type, item.itemType, item.tags, item.url)}
                      typeLabel={getTypeLabel(item.type, item.itemType, item.tags, item.url)}
                      updatedAt={item.updatedAt}
                      embeddingStatusMap={embeddingStatusMap}
                      documentActions={libraryDocumentActions}
                      onOpen={() => handleOpenDocument(item)}
                      isFavorite={isFavorite(item.id, item.itemType)}
                      onToggleFavorite={() => handleToggleFavorite(item)}
                      isSourceOfTruth={sourceOfTruthIdSet.has(item.id)}
                    />
                  </div>
                </div>
              </DraggableLibraryItem>
            );
          }
          
          // Custom rendering for references
          return (
            <DraggableLibraryItem key={item.id} item={item}>
            <Card className={`group hover:shadow-sm transition-shadow cursor-pointer ${isSelected ? 'ring-2 ring-primary border-primary' : ''}`} onClick={() => handleOpenDocument(item)}>
          <CardContent className="p-2 sm:p-2.5 md:p-3">
            <div className="flex items-center gap-3 min-w-0">
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => toggleItemSelection(item.id, false)}
                onPointerDown={(e) => {
                  if (e.shiftKey) {
                    e.preventDefault();
                    toggleItemSelection(item.id, true);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                data-testid={`checkbox-list-item-${item.id}`}
                className="h-4 w-4 shrink-0"
              />
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {getTypeIcon(item.type, item.itemType, item.tags, item.url)}
                <Badge variant="secondary" className="text-xs px-1.5 py-0.5 whitespace-nowrap">
                  Ref
                </Badge>
              </div>

              <div className="flex-1 min-w-0 overflow-hidden">
                <h3 className="font-medium text-sm truncate hover:text-primary transition-colors" title={item.title}>
                  {truncateTitle(item.title)}
                </h3>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0 hidden md:inline">
                  {formatDistanceToNow(new Date(item.updatedAt), { addSuffix: true })}
                </span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {item.url && (
                      <DropdownMenuItem onClick={() => window.open(item.url || undefined, "_blank")}>
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Open Link
                      </DropdownMenuItem>
                    )}
                    {isExpertItem(item.type, item.tags) && (
                      <DropdownMenuItem onClick={() => handleManageChildDocs(item)}>
                        <BookMarked className="h-4 w-4 mr-2" />
                        View Works
                      </DropdownMenuItem>
                    )}
                    {renderFavoriteMenuItem(item)}
                    <DropdownMenuItem onClick={() => handleOpenInspector(item)}>
                      <Link2 className="h-4 w-4 mr-2" />
                      Manage Links
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <div className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                      <EntityLockButton
                        entityType="document"
                        entityId={item.id}
                        isLocked={item.isLocked === true}
                        size="menu"
                        customTitle={item.isLocked ? "Unlock document" : "Lock document"}
                      />
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleOpenInspector(item)}>
                      <Folder className="h-4 w-4 mr-2" />
                      Move to Folder...
                    </DropdownMenuItem>
                    {item.folderId && (
                      <DropdownMenuItem onClick={() => handleMoveToFolder(item, null)}>
                        <X className="h-4 w-4 mr-2" />
                        Remove from Folder
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </CardContent>
        </Card>
            </DraggableLibraryItem>
        );
      })}
    </div>
  ); 

  
  
   /* const renderTableView = () => (
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort("title")}
                >
                  <div className="flex items-center gap-1">
                    Title
                    {sortField === "title" && (
                      sortDirection === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    )}
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort("type")}
                >
                  <div className="flex items-center gap-1">
                    Type
                    {sortField === "type" && (
                      sortDirection === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    )}
                  </div>
                </TableHead>
                <TableHead className="hidden md:table-cell">Folder</TableHead>
                <TableHead className="hidden lg:table-cell">Links</TableHead>
                <TableHead className="hidden lg:table-cell">Tags</TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort("updatedAt")}
                >
                  <div className="flex items-center gap-1">
                    Updated
                    {sortField === "updatedAt" && (
                      sortDirection === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    )}
                  </div>
                </TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
          <TableBody>
            {displayItems.map((displayItem) => {
              // Handle SPACES root folder at library root
              if (displayItem.type === 'spaces-root') {
                const spacesRoot = displayItem.data as SpacesRootFolder;
                return (
                  <TableRow 
                    key="spaces-root-table"
                    className="group cursor-pointer hover:bg-purple-50/50 bg-purple-50/30 dark:bg-purple-950/30 dark:hover:bg-purple-900/40" 
                    onClick={() => handleFolderClick("spaces")}
                    data-testid="spaces-root-folder-table"
                  >
                    <TableCell className="w-8" />
                    <TableCell className="min-w-[150px] max-w-[200px] sm:min-w-0 sm:max-w-none">
                      <div className="flex items-center gap-2 min-w-0">
                        <Brain className="h-4 w-4 text-purple-600 shrink-0" />
                        <span className="font-medium hover:text-primary transition-colors truncate">{spacesRoot.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs bg-purple-100 border-purple-300 text-purple-800 dark:bg-purple-900/50 dark:border-purple-700 dark:text-purple-200">
                        Virtual
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">—</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">—</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">—</span>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {spacesRoot.spaceCount} {spacesRoot.spaceCount === 1 ? 'space' : 'spaces'}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <span className="text-xs text-muted-foreground">—</span>
                    </TableCell>
                  </TableRow>
                );
              }

              if (displayItem.type === 'folder') {
                const folder = displayItem.data as LibraryFolder;
                const itemCount = folderItemCounts.get(folder.id) || 0;
                return (
                  <DroppableLibraryFolder key={`folder-${folder.id}`} folder={folder}>
                  <TableRow 
                    className="group cursor-pointer hover:bg-amber-50/50 bg-amber-50/30 dark:bg-amber-950/30 dark:hover:bg-amber-900/40" 
                    onClick={() => handleFolderClick(folder.id)}
                  >
                    <TableCell className="w-8" />
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FolderOpen className="h-4 w-4 text-amber-600" />
                        <span className="font-medium hover:text-primary transition-colors">{folder.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs bg-amber-100 border-amber-300 text-amber-800 dark:bg-amber-900/50 dark:border-amber-700 dark:text-amber-200">
                        Folder
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="text-xs text-muted-foreground">—</span>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <span className="text-xs text-muted-foreground">—</span>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <span className="text-xs text-muted-foreground">—</span>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {itemCount} {itemCount === 1 ? 'item' : 'items'}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openNewFolderDialog(folder.id); }}>
                            <FolderPlus className="h-4 w-4 mr-2" />
                            New subfolder
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditFolderDialog(folder); }}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Rename folder
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={(e) => { e.stopPropagation(); handleDeleteFolderFromLibrary(folder); }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete folder
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                  </DroppableLibraryFolder>
                );
              }

              // Handle virtual space folders in table view
              if (displayItem.type === 'space-folder') {
                const spaceFolder = displayItem.data as SpaceVirtualFolder;
                const spaceItemCount = unifiedItems.filter(item => 
                  Array.isArray(item.linkedSpaceIds) && item.linkedSpaceIds.includes(spaceFolder.spaceId)
                ).length;
                return (
                  <DroppableLibrarySpace key={`space-folder-${spaceFolder.id}`} spaceId={spaceFolder.spaceId}>
                  <TableRow 
                    className="group cursor-pointer hover:bg-purple-50/50 bg-purple-50/30 dark:bg-purple-950/30 dark:hover:bg-purple-900/40" 
                    onClick={() => handleFolderClick(spaceFolder.id)}
                    data-testid={`space-folder-table-${spaceFolder.spaceId}`}
                  >
                    <TableCell className="w-8" />
                    <TableCell className="min-w-[150px] max-w-[200px] sm:min-w-0 sm:max-w-none">
                      <div className="flex items-center gap-2 min-w-0">
                        <Brain className="h-4 w-4 text-purple-600 shrink-0" />
                        <span className="font-medium hover:text-primary transition-colors truncate">{spaceFolder.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs bg-purple-100 border-purple-300 text-purple-800 dark:bg-purple-900/50 dark:border-purple-700 dark:text-purple-200">
                        Space
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">—</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">—</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">—</span>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {spaceItemCount} {spaceItemCount === 1 ? 'document' : 'documents'}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <span className="text-xs text-muted-foreground">—</span>
                    </TableCell>
                  </TableRow>
                  </DroppableLibrarySpace>
                );
              }
              
              const item = displayItem.data as UnifiedLibraryItem;
              return (
                <DraggableTableDocumentRow key={item.id} item={item} onClick={() => handleOpenDocument(item)}>
                  <TableCell className="min-w-[150px] max-w-[200px] sm:min-w-0 sm:max-w-none">
                    <div className="flex items-center gap-2 min-w-0">
                      {getTypeIcon(item.type, item.itemType, item.tags, item.url)}
                      <span className="font-medium hover:text-primary transition-colors truncate">{item.title}</span>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <Badge variant={item.itemType === "note" ? "default" : "secondary"} className="text-xs">
                      {getTypeLabel(item.type, item.itemType, item.tags, item.url)}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {item.folderId ? (
                      <Badge variant="outline" className="text-xs gap-1 bg-amber-50 border-amber-200 text-amber-700">
                        <Folder className="h-3 w-3" />
                        {folders.find(f => f.id === item.folderId)?.name || "Folder"}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell" onClick={(e) => e.stopPropagation()}>{renderLinksSection(item)}</TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex gap-1">
                      {(Array.isArray(item.tags) ? item.tags : []).slice(0, 2).map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                      {Array.isArray(item.tags) && item.tags.length > 2 && (
                        <span className="text-xs text-muted-foreground">+{item.tags.length - 2}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDistanceToNow(new Date(item.updatedAt), { addSuffix: true })}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {item.itemType === "note" ? (
                          <>
                            <DropdownMenuItem onClick={() => handleOpenDocument(item)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {renderFavoriteMenuItem(item)}
                            <DropdownMenuItem onClick={() => handleRename(item)}>
                              <Type className="h-4 w-4 mr-2" />
                              Rename
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDuplicate(item)}>
                              <Copy className="h-4 w-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => void copyPublicLink(item.id)}>
                              <Globe className="h-4 w-4 mr-2" />
                              Copy public link
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleMerge(item)}>
                              <Merge className="h-4 w-4 mr-2" />
                              Merge into...
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleOpenInspector(item)}>
                              <Link2 className="h-4 w-4 mr-2" />
                              Manage Links
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleOpenInspector(item)}>
                              <Folder className="h-4 w-4 mr-2" />
                              Move to Folder...
                            </DropdownMenuItem>
                            {item.folderId && (
                              <DropdownMenuItem onClick={() => handleMoveToFolder(item, null)}>
                                <X className="h-4 w-4 mr-2" />
                                Remove from Folder
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(item)}>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </>
                        ) : (
                          <>
                            {item.url && (
                              <DropdownMenuItem onClick={() => window.open(item.url || undefined, "_blank")}>
                                <ExternalLink className="h-4 w-4 mr-2" />
                                Open Link
                              </DropdownMenuItem>
                            )}
                        {isExpertItem(item.type, item.tags) && (
                          <DropdownMenuItem onClick={() => handleManageChildDocs(item)}>
                            <BookMarked className="h-4 w-4 mr-2" />
                            View Works
                          </DropdownMenuItem>
                        )}
                        {renderFavoriteMenuItem(item)}
                        <DropdownMenuItem onClick={() => handleOpenInspector(item)}>
                          <Link2 className="h-4 w-4 mr-2" />
                          Manage Links
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleOpenInspector(item)}>
                          <Folder className="h-4 w-4 mr-2" />
                          Move to Folder...
                        </DropdownMenuItem>
                        {item.folderId && (
                          <DropdownMenuItem onClick={() => handleMoveToFolder(item, null)}>
                            <X className="h-4 w-4 mr-2" />
                            Remove from Folder
                          </DropdownMenuItem>
                        )}
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
        </div>
    </div>
  ); */

  const renderTableView = () => (
  <div className="border rounded-lg overflow-hidden bg-background">
    <div className="overflow-x-auto">
      <Table>
        <TableHeader className="bg-muted/30">
          <TableRow>
            <TableHead className="w-8" aria-label="Drag" />
            {/* Title - Takes all available space */}
            <TableHead
              className="cursor-pointer hover:bg-muted/50 w-full min-w-[200px]"
              onClick={() => handleSort("title")}
            >
              <div className="flex items-center gap-1">
                Title
                {sortField === "title" && (sortDirection === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
              </div>
            </TableHead>
            
            {/* Type - Fixed width to prevent title squashing */}
            <TableHead
              className="cursor-pointer hover:bg-muted/50 w-[100px]"
              onClick={() => handleSort("type")}
            >
              <div className="flex items-center gap-1">
                Type
                {sortField === "type" && (sortDirection === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
              </div>
            </TableHead>

            {/* Hidden on Tablet (Problem range 767-950) */}
            <TableHead className="hidden xl:table-cell w-[140px]">Folder</TableHead>
            <TableHead className="hidden lg:table-cell w-[100px]">Links</TableHead>
            
            {/* Updated - Fixed width */}
            <TableHead
              className="cursor-pointer hover:bg-muted/50 w-[120px] hidden sm:table-cell"
              onClick={() => handleSort("updatedAt")}
            >
              <div className="flex items-center gap-1">
                Updated
                {sortField === "updatedAt" && (sortDirection === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
              </div>
            </TableHead>
            
            <TableHead className="w-10"></TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {browseDocumentItems.map((item) => {
            const isSelected = selectedItems.has(item.id);
            
            // Use DocumentItem for notes in table view
            if (item.itemType === "note") {
              const doc = item.originalItem as KnowledgeBaseItem;
              return (
                <DraggableTableDocumentRow key={item.id} item={item} onClick={() => handleDocumentClick(doc.id, doc.isLocked)}>
                  <TableCell className="max-w-0" onClick={(e) => { e.stopPropagation(); handleDocumentClick(doc.id, doc.isLocked); }}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleItemSelection(item.id, false)}
                        onPointerDown={(e) => {
                          if (e.shiftKey) {
                            e.preventDefault();
                            toggleItemSelection(item.id, true);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="shrink-0"
                        data-testid={`checkbox-table-item-${item.id}`}
                      />
                      <div className="shrink-0">
                        {getTypeIcon(item.type, item.itemType, item.tags, item.url)}
                      </div>
                      <span className="font-medium truncate cursor-pointer hover:text-primary transition-colors" title={item.title}>
                        {truncateTableTitle(item.title)}
                      </span>
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <Badge variant="default" className="text-[10px] uppercase tracking-tight">
                      {getTypeLabel(doc.type || "note", "note", [], null)}
                    </Badge>
                  </TableCell>

                  <TableCell className="hidden xl:table-cell">
                    {doc.folderId ? (
                      <div className="flex items-center gap-1 text-xs text-amber-700 truncate max-w-[120px]">
                        <Folder className="h-3 w-3 shrink-0" />
                        <span className="truncate">{folders.find(f => f.id === doc.folderId)?.name}</span>
                      </div>
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>

                  <TableCell className="hidden lg:table-cell" onClick={(e) => e.stopPropagation()}>
                    {!isCompactBrowseUi ? renderLinksSection(item) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>

                  <TableCell className="hidden sm:table-cell text-xs text-muted-foreground whitespace-nowrap">
                    {doc.updatedAt ? formatDistanceToNow(new Date(doc.updatedAt), { addSuffix: true }) : "Unknown"}
                  </TableCell>

                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <LibraryBrowseDocumentMenu
                      item={unifiedItemToDocumentActions(item)}
                      embeddingStatusMap={embeddingStatusMap}
                      documentActions={libraryDocumentActions}
                      isFavorite={isFavorite(item.id, item.itemType)}
                      onToggleFavorite={() => handleToggleFavorite(item)}
                    />
                  </TableCell>
                </DraggableTableDocumentRow>
              );
            }
            
            // Custom rendering for references
            return (
              <DraggableTableDocumentRow key={item.id} item={item} onClick={() => handleOpenDocument(item)}>
                <TableCell className="max-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleItemSelection(item.id, false)}
                      onPointerDown={(e) => {
                        if (e.shiftKey) {
                          e.preventDefault();
                          toggleItemSelection(item.id, true);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="shrink-0"
                      data-testid={`checkbox-table-item-${item.id}`}
                    />
                    <div className="shrink-0">{getTypeIcon(item.type, item.itemType, item.tags, item.url)}</div>
                    <span className="font-medium truncate" title={item.title}>{truncateTableTitle(item.title)}</span>
                  </div>
                </TableCell>
                
                <TableCell>
                  <Badge variant="secondary" className="text-[10px] uppercase tracking-tight">
                    {item.itemType}
                  </Badge>
                </TableCell>

                <TableCell className="hidden xl:table-cell">
                  {item.folderId ? (
                    <div className="flex items-center gap-1 text-xs text-amber-700 truncate max-w-[120px]">
                      <Folder className="h-3 w-3 shrink-0" />
                      <span className="truncate">{folders.find(f => f.id === item.folderId)?.name}</span>
                    </div>
                  ) : <span className="text-muted-foreground">—</span>}
                </TableCell>

                <TableCell className="hidden lg:table-cell" onClick={(e) => e.stopPropagation()}>
                   {renderLinksSection(item)}
                </TableCell>

                <TableCell className="hidden sm:table-cell text-xs text-muted-foreground whitespace-nowrap">
                  {formatDistanceToNow(new Date(item.updatedAt), { addSuffix: true })}
                </TableCell>

                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {item.url && (
                        <DropdownMenuItem onClick={() => window.open(item.url || undefined, "_blank")}>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Open Link
                        </DropdownMenuItem>
                      )}
                      {isExpertItem(item.type, item.tags) && (
                        <DropdownMenuItem onClick={() => handleManageChildDocs(item)}>
                          <BookMarked className="h-4 w-4 mr-2" />
                          View Works
                        </DropdownMenuItem>
                      )}
                      {renderFavoriteMenuItem(item)}
                      <DropdownMenuItem onClick={() => handleOpenInspector(item)}>
                        <Link2 className="h-4 w-4 mr-2" />
                        Manage Links
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <div className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                        <EntityLockButton
                          entityType="document"
                          entityId={item.id}
                          isLocked={item.isLocked === true}
                          size="menu"
                          customTitle={item.isLocked ? "Unlock document" : "Lock document"}
                        />
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleOpenInspector(item)}>
                        <Folder className="h-4 w-4 mr-2" />
                        Move to Folder...
                      </DropdownMenuItem>
                      {item.folderId && (
                        <DropdownMenuItem onClick={() => handleMoveToFolder(item, null)}>
                          <X className="h-4 w-4 mr-2" />
                          Remove from Folder
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </DraggableTableDocumentRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  </div>
);

  const renderDocumentsPanel = () => {
    if (useVirtualDocumentList) {
      return (
        <div
          className={cn(
            "w-full",
            isBrowseDocumentsPending && "opacity-60 pointer-events-none"
          )}
        >
          <LibraryDocumentsVirtualList
            items={browseDocumentItems}
            renderItem={renderBrowseListRow}
          />
        </div>
      );
    }
    if (viewMode === "grid") return renderGridView();
    if (viewMode === "list") return renderListView();
    return renderTableView();
  };

  // Combine built-in and user-created tools
  const allAITools = useMemo(() => {
    const tools: AITool[] = ALL_AI_TOOLS.filter((t) => t.tier !== "agent");

    if (userWorkflows.length > 0) {
      tools.push(...userWorkflows);
    }

    return tools;
  }, [userWorkflows]);

  const filteredAITools = useMemo(() => {
    let filtered = allAITools;
    
    if (aiToolkitFilter !== "all") {
      filtered = filtered.filter(tool => tool.tier === aiToolkitFilter);
    }
    
    if (aiToolkitCategory !== "All") {
      filtered = filtered.filter(tool => tool.category === aiToolkitCategory);
    }
    
    if (blueprintSearch.trim()) {
      const query = blueprintSearch.toLowerCase();
      filtered = filtered.filter(tool => 
        tool.name.toLowerCase().includes(query) ||
        tool.description.toLowerCase().includes(query) ||
        tool.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  }, [allAITools, aiToolkitFilter, aiToolkitCategory, blueprintSearch]);

  const handleAIToolClick = (tool: AITool) => {
    setSelectedAITool(tool);
    if (tool.tier === 'quick-prompt') {
      setQuickPromptModalOpen(true);
    } else if (tool.tier === 'workflow') {
      // Check if it's a user-created workflow
      const isUserCreated = tool.createdBy === 'user' || userWorkflows.some(w => w.id === tool.id);
      if (isUserCreated) {
        setEditingWorkflow(tool as Workflow);
      } else {
        // Built-in workflow - just open for viewing/using
        setEditingWorkflow(tool as Workflow);
      }
      setWorkflowBuilderOpen(true);
    }
  };

  const handleQuickPromptExecute = (filledPrompt: string) => {
    navigate(`/chat?prompt=${encodeURIComponent(filledPrompt)}`);
    setQuickPromptModalOpen(false);
    setSelectedAITool(null);
  };

  const renderAIToolkitView = () => (
    <div className="space-y-6">
      {/* Top search/sorting controls - only show for "all" filter */}
      {aiToolkitFilter === "all" && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex gap-2 w-full items-center">
              <Select value={aiToolkitCategory} onValueChange={setAiToolkitCategory}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {AI_TOOL_CATEGORIES.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="relative flex-1 sm:w-48">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search tools..."
                  value={blueprintSearch}
                  onChange={(e) => setBlueprintSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              
              <div className="flex items-center border rounded-md">
                <Button
                  variant={aiToolkitViewMode === "grid" ? "secondary" : "ghost"}
                  size="icon"
                  className="rounded-r-none"
                  onClick={() => setAiToolkitViewMode("grid")}
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant={aiToolkitViewMode === "list" ? "secondary" : "ghost"}
                  size="icon"
                  className="rounded-l-none"
                  onClick={() => setAiToolkitViewMode("list")}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {aiToolkitFilter === "all" ? (
        <div className="space-y-8">
          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-green-500" />
                <h3 className="font-semibold">Quick Prompts</h3>
                <span className="text-sm text-muted-foreground">Simple, one-shot AI shortcuts</span>
              </div>
            </div>
            <AIToolList 
              tools={filteredAITools.filter(t => t.tier === 'quick-prompt').slice(0, 4)} 
              onToolClick={handleAIToolClick}
              viewMode={aiToolkitViewMode}
            />
          </section>

          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Bot className="h-5 w-5 text-purple-500" />
                <h3 className="font-semibold">AI Agents</h3>
                <span className="text-sm text-muted-foreground">Specialized agents for complex tasks</span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate("/ai-agents")}
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                Manage Agents
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              AI Agents live in Tools. Create, organize, and share agents from the AI Agents hub.
            </p>
          </section>

          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <GitCompare className="h-5 w-5 text-orange-500" />
                <h3 className="font-semibold">Workflows</h3>
                <span className="text-sm text-muted-foreground">Visual flows connecting agents and tools</span>
              </div>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => {
                  setEditingWorkflow(null);
                  setWorkflowBuilderOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-1" />
                Create Workflow
              </Button>
            </div>
            <AIToolList 
              tools={filteredAITools.filter(t => t.tier === 'workflow').slice(0, 4)} 
              onToolClick={handleAIToolClick}
              viewMode={aiToolkitViewMode}
            />
          </section>

        </div>
      ) : aiToolkitFilter === 'workflow' ? (
        <div className="space-y-4">
          {/* Row 1: Title and Button */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <GitCompare className="h-5 w-5 text-orange-500" />
              <h2 className="text-lg font-semibold">Workflows</h2>
            </div>
            <Button 
              onClick={() => {
                setEditingWorkflow(null);
                setWorkflowBuilderOpen(true);
              }}
              className="gap-1"
              data-testid="button-create-workflow"
            >
              <Plus className="h-4 w-4" />
              Create Workflow
            </Button>
          </div>

          {/* Row 2: Search and Sorting (moved from top) */}
          <div className="flex items-center gap-2 flex-wrap mt-4">
            <Select value={aiToolkitCategory} onValueChange={setAiToolkitCategory}>
              <SelectTrigger className="w-[140px] h-9">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {AI_TOOL_CATEGORIES.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search workflows..."
                value={blueprintSearch}
                onChange={(e) => setBlueprintSearch(e.target.value)}
                className="pl-8 h-9 text-sm"
              />
            </div>
            
            <div className="flex items-center border rounded-md">
              <Button
                variant={aiToolkitViewMode === "grid" ? "secondary" : "ghost"}
                size="icon"
                className="h-7 w-7"
                onClick={() => setAiToolkitViewMode("grid")}
                title="Grid view"
              >
                <LayoutGrid className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant={aiToolkitViewMode === "list" ? "secondary" : "ghost"}
                size="icon"
                className="h-7 w-7"
                onClick={() => setAiToolkitViewMode("list")}
                title="List view"
              >
                <List className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant={showBuiltInWorkflows ? "secondary" : "outline"}
              size="sm"
              onClick={() => setShowBuiltInWorkflows(!showBuiltInWorkflows)}
              data-testid="button-toggle-builtin-workflows"
            >
              {showBuiltInWorkflows ? "Hide" : "Show"} Built-in Workflows
            </Button>
            <span className="text-sm text-muted-foreground">
              {filteredAITools.filter(t => showBuiltInWorkflows || !t.id.startsWith('workflow-')).length} workflow{filteredAITools.length !== 1 ? "s" : ""}
            </span>
          </div>

          {(isLoadingUserWorkflows) && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}
          {workflowsError && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-4">
              <p className="text-sm text-destructive">{workflowsError}</p>
            </div>
          )}
          <AIToolList 
            tools={showBuiltInWorkflows ? filteredAITools : filteredAITools.filter(t => !t.id.startsWith('workflow-'))} 
            onToolClick={handleAIToolClick}
            viewMode={aiToolkitViewMode}
          />
        </div>
      ) : (
        <div className="space-y-4">
          <AIToolList 
            tools={filteredAITools} 
            onToolClick={handleAIToolClick}
            viewMode={aiToolkitViewMode}
          />
        </div>
      )}

      {filteredAITools.length === 0 && (
        <EmptyState
          icon={Brain}
          title="No tools found"
          description="Try adjusting your search or filters"
        />
      )}
    </div>
  );

  const renderBlueprintsView = () => (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-end">
          <div className="flex items-center gap-2">
            <div className="relative w-full sm:w-48">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search blueprints..."
                value={blueprintSearch}
                onChange={(e) => setBlueprintSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Select value={blueprintSortField} onValueChange={(v) => setBlueprintSortField(v as BlueprintSortField)}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="category">Category</SelectItem>
                <SelectItem value="type">Type</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setBlueprintSortDirection(blueprintSortDirection === "asc" ? "desc" : "asc")}
            >
              {blueprintSortDirection === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
            </Button>
            
            <div className="flex items-center border rounded-md">
              <Button
                variant={blueprintViewMode === "grid" ? "secondary" : "ghost"}
                size="icon"
                className="rounded-r-none"
                onClick={() => setBlueprintViewMode("grid")}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={blueprintViewMode === "list" ? "secondary" : "ghost"}
                size="icon"
                className="rounded-l-none"
                onClick={() => setBlueprintViewMode("list")}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {blueprintViewMode === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredBlueprints.map((blueprint) => (
          <Card 
            key={blueprint.id} 
            className="group hover:shadow-md transition-all cursor-pointer overflow-hidden"
            onClick={() => handleCreateFromBlueprint(blueprint)}
          >
            <div className={`h-2 ${blueprint.color}`} />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className={`p-2 rounded-lg ${blueprint.color} bg-opacity-10`}>
                  <div className="text-white">
                    {getBlueprintIcon(blueprint.icon)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={blueprint.type === "edition" ? "default" : "secondary"} className="text-xs">
                    {blueprint.type === "edition" ? "Edition" : "Research"}
                  </Badge>
                  {blueprint.tier === "pro" && (
                    <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/30">
                      <Zap className="h-3 w-3 mr-1" />
                      Pro
                    </Badge>
                  )}
                  {blueprint.tier === "premium" && (
                    <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-600 border-purple-500/30">
                      <Lock className="h-3 w-3 mr-1" />
                      Premium
                    </Badge>
                  )}
                </div>
              </div>
              
              <h3 className="font-semibold text-base mb-1">{blueprint.name}</h3>
              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                {blueprint.description}
              </p>
              
              <div className="flex items-center justify-between">
                <Badge variant="outline" className="text-xs">
                  {blueprint.category}
                </Badge>
                <Button 
                  size="sm" 
                  variant="ghost"
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateFromBlueprint(blueprint);
                  }}
                >
                  Use Template
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredBlueprints.map((blueprint) => (
            <Card 
              key={blueprint.id} 
              className="group hover:shadow-md transition-all cursor-pointer"
              onClick={() => handleCreateFromBlueprint(blueprint)}
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`p-2 rounded-lg ${blueprint.color} bg-opacity-20 flex-shrink-0`}>
                  <div className="text-white">
                    {getBlueprintIcon(blueprint.icon)}
                  </div>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-sm truncate">{blueprint.name}</h3>
                    <Badge variant={blueprint.type === "edition" ? "default" : "secondary"} className="text-xs flex-shrink-0">
                      {blueprint.type === "edition" ? "Edition" : "Research"}
                    </Badge>
                    {blueprint.tier === "pro" && (
                      <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/30 flex-shrink-0">
                        <Zap className="h-3 w-3 mr-1" />
                        Pro
                      </Badge>
                    )}
                    {blueprint.tier === "premium" && (
                      <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-600 border-purple-500/30 flex-shrink-0">
                        <Lock className="h-3 w-3 mr-1" />
                        Premium
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{blueprint.description}</p>
                </div>
                
                <Badge variant="outline" className="text-xs flex-shrink-0">
                  {blueprint.category}
                </Badge>
                
                <Button 
                  size="sm" 
                  variant="ghost"
                  className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateFromBlueprint(blueprint);
                  }}
                >
                  Use
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {filteredBlueprints.length === 0 && (
        <EmptyState
          icon={Sparkles}
          title="No blueprints found"
          description="Try adjusting your search or filter"
        />
      )}
    </div>
  );

  if (isLoading && librarySection === "content") {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 flex flex-col h-full">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold flex items-center gap-2 flex-wrap">
            <BookOpen className="h-6 w-6" />
            Library
            {librarySection === "ai-toolkit" && aiToolkitFilter === "workflow" && (
              <>
                <span className="text-muted-foreground font-normal">/</span>
                <span className="flex items-center gap-1">
                  <GitCompare className="h-5 w-5 text-orange-500" />
                  Workflows
                </span>
              </>
            )}
            {librarySection === "ai-toolkit" && aiToolkitFilter === "all" && (
              <>
                <span className="text-muted-foreground font-normal">/</span>
                <span className="flex items-center gap-1">
                  <Brain className="h-5 w-5 text-purple-500" />
                  AI Toolkit
                </span>
              </>
            )}
            {filterSpaceId && (
              <Badge variant="secondary" className="ml-2 gap-1">
                <Target className="h-3 w-3" />
                {spaces.find(s => s.id === filterSpaceId)?.name || "Space"}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 p-0 ml-1"
                  onClick={() => navigate("/library")}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            )}
            {filterTopicId && (
              <Badge variant="secondary" className="ml-2 gap-1">
                <Layers className="h-3 w-3" />
                {topics.find(t => t.id === filterTopicId)?.name || "Topic"}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 p-0 ml-1"
                  onClick={() => navigate("/library")}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            )}
          </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {/* All subtitle paragraphs commented out:
              selectedFolderId==="spaces" ? "Browse documents organized by Mastermind Space"
              isInsideSpecificSpace ? `Documents linked to "${space name}"`
              filterSpaceId ? "Documents linked to this Space"
              filterTopicId ? "Documents linked to this Topic"
              librarySection==="ai-toolkit" ? "Pre-built prompts, agents, and research templates"
              librarySection==="super-prompts" ? "Your custom prompt templates for quick AI interactions"
              else ? "Ready-to-use starting points for new Spaces" */}
              {""}
            </p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full lg:w-auto">
          {librarySection === "content" && (
            <Tabs value={activeFilter} onValueChange={(v) => setActiveFilter(v as LibraryFilter)} className="w-full sm:w-auto">
              <TabsList className="w-full sm:w-auto flex justify-start overflow-x-auto">
                <TabsTrigger value="all">
                  All ({baseFilteredItems.length})
                </TabsTrigger>
                <TabsTrigger value="notes">
                  Notes ({baseFilteredItems.filter((i) => i.itemType === "note").length})
                </TabsTrigger>
                <TabsTrigger value="references">
                  References ({baseFilteredItems.filter((i) => i.itemType === "reference" && !isExpertItem(i.type, i.tags)).length})
                </TabsTrigger>
                <TabsTrigger value="experts" className="gap-1">
                  <User className="h-3 w-3" />
                  Experts ({baseFilteredItems.filter((i) => isExpertItem(i.type, i.tags)).length})
                </TabsTrigger>
                <TabsTrigger value="tutorials" className="gap-1">
                  <Video className="h-3 w-3" />
                  Tutorials ({tutorialVideos.length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {librarySection === "blueprints" && (
            <Tabs value={blueprintFilter} onValueChange={(v) => setBlueprintFilter(v as BlueprintFilter)} className="w-full sm:w-auto">
              <TabsList className="w-full sm:w-auto flex justify-start overflow-x-auto">
              <TabsTrigger value="all">
                All ({ALL_BLUEPRINTS.length})
              </TabsTrigger>
              <TabsTrigger value="edition">
                Editions ({EDITION_BLUEPRINTS.length})
              </TabsTrigger>
              <TabsTrigger value="research">
                Research ({RESEARCH_BLUEPRINTS.length})
              </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {librarySection === "content" && (
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="w-full sm:w-auto">
                <Button data-testid="button-add-new" className="w-full sm:w-auto">
                  <Plus className="h-4 w-4 mr-2" />
                  Add New
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {/*<DropdownMenuItem onClick={() => {
                  setSelectedDocumentId(null);
                  setSelectedReferenceId(null);
                }}>
                  <FileText className="h-4 w-4 mr-2" />
                  New Note
                </DropdownMenuItem>*/}
                <DropdownMenuItem onClick={() => setAddReferenceDialogOpen(true)}>
                  <Link2 className="h-4 w-4 mr-2" />
                  Add Reference
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openNewFolderDialog(null)}>
                  <FolderPlus className="h-4 w-4 mr-2" />
                  New Folder
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                  <FileUploadToNote
                    spaceId={selectedSpaceId || null}
                    folderId={selectedFolderId && !selectedFolderId.startsWith("space-") && selectedFolderId !== "spaces" ? selectedFolderId : null}
                    topicId={null}
                    buttonLabel="Upload Files"
                    multiple={true}
                    onComplete={() => {
                      queryClient.invalidateQueries({ 
                        predicate: (query) => {
                          const key = query.queryKey[0];
                          return typeof key === 'string' && (
                            key === '/api/knowledge-base' || 
                            key === '/api/knowledgebase' ||
                            key === 'knowledgebase' ||
                            key === 'knowledgebase-summary'
                          );
                        }
                      });
                    }}
                  />
                </div>
                {showDrivePicker && (
                  <GoogleDrivePicker
                    multiple={true}
                    isFinalConfirmation={true}
                    extractTextOnly={true}
                    onFilesSelected={async (files) => {
                      // Validate files - skip invalid ones
                      const validFiles = files.filter((file: any) => {
                        // Check if it's an extracted text file (has text property)
                        if (file.text && typeof file.text === 'string' && file.text.trim().length > 0) {
                          return true;
                        }
                        // Skip files with errors or no text
                        return false;
                      });

                      if (validFiles.length === 0) {
                        toast({
                          title: "No valid files",
                          description: "No files with extractable text were found.",
                          variant: "destructive",
                        });
                        return;
                      }

                      // Hide picker and show loading
                      setShowDrivePicker(false);
                      setIsCreatingNotes(true);
                      setNoteCreationProgress({
                        total: validFiles.length,
                        completed: 0,
                        current: '',
                        failed: [],
                      });

                      // Extract spaceId if we're inside a space folder
                      const actualSpaceId = selectedSpaceId || null;
                      // Extract folderId (only if it's a real folder, not a space folder)
                      const actualFolderId = selectedFolderId && !selectedFolderId.startsWith("space-") && selectedFolderId !== "spaces"
                        ? selectedFolderId
                        : null;

                      const driveMirrorFolderName = getGoogleDriveImportRootFolderName(validFiles);
                      let targetFolderId: string | null = actualFolderId;

                      if (driveMirrorFolderName) {
                        setNoteCreationProgress(prev => prev ? {
                          ...prev,
                          current: `Preparing folder "${driveMirrorFolderName}"…`,
                          completed: 0,
                        } : null);
                        try {
                          const folderListKey = [
                            "/api/library-folders",
                            user?.id,
                            folderWorkspaceParam === undefined ? "all" : folderWorkspaceParam ?? "personal",
                          ] as const;
                          const refreshedFolders = user
                            ? await queryClient.fetchQuery<LibraryFolder[]>({
                                queryKey: [...folderListKey],
                                queryFn: async () => foldersService.getFolders(folderWorkspaceParam),
                              })
                            : folders;
                          targetFolderId = await ensureLibraryFolderByName(
                            driveMirrorFolderName,
                            actualFolderId,
                            currentWorkspaceId,
                            refreshedFolders
                          );
                          await queryClient.invalidateQueries({ queryKey: ["/api/library-folders"] });
                        } catch (e: any) {
                          console.error("Drive import: could not ensure mirror folder", e);
                          toast({
                            title: "Folder setup failed",
                            description: e instanceof Error ? e.message : "Could not create or find the import folder.",
                            variant: "destructive",
                          });
                          setIsCreatingNotes(false);
                          setNoteCreationProgress(null);
                          setShowDrivePicker(true);
                          return;
                        }
                      }

                      // Create notes for each file in the current folder/space
                      let successCount = 0;
                      let failCount = 0;

                      for (let i = 0; i < validFiles.length; i++) {
                        const file = validFiles[i];
                        const filename = file.filename || `Untitled Document ${i + 1}`;

                        setNoteCreationProgress(prev => prev ? {
                          ...prev,
                          current: `Creating note: ${filename}`,
                          completed: i,
                        } : null);

                        try {
                          // Type guard: check if file has text property
                          const fileContent = 'text' in file ? file.text : '';

                          // Create document using knowledgeBaseService with selectedFolderId and spaceId
                          await knowledgeBaseService.createKnowledgeBaseItem({
                            title: filename,
                            content: fileContent,
                            type: 'note', // Default type
                            spaceId: actualSpaceId, // Use space ID if inside a space folder
                            topicId: null, // Default - no topic
                            tagIds: [], // Default - no tags
                            folderId: targetFolderId,
                          }, currentWorkspaceId ?? null);

                          successCount++;
                          // Invalidate all knowledge base queries to refresh Library page
                          queryClient.invalidateQueries({ 
                            predicate: (query) => {
                              const key = query.queryKey[0];
                              return typeof key === 'string' && (
                                key === '/api/knowledge-base' || 
                                key === '/api/knowledgebase' ||
                                key === 'knowledgebase' ||
                                key === 'knowledgebase-summary'
                              );
                            }
                          });
                        } catch (error: any) {
                          console.error(`Error creating note for ${filename}:`, error);
                          failCount++;
                          setNoteCreationProgress(prev => prev ? {
                            ...prev,
                            failed: [...prev.failed, filename],
                          } : null);
                        }
                      }

                      // Update final progress
                      setNoteCreationProgress(prev => prev ? {
                        ...prev,
                        completed: validFiles.length,
                        current: 'Complete!',
                      } : null);

                      // Show completion message
                      if (successCount > 0) {
                        let locationMessage = '';
                        if (actualSpaceId) {
                          const space = spaces.find(s => s.id === actualSpaceId);
                          locationMessage = ` in space "${space?.name || 'space'}"`;
                        } else if (targetFolderId) {
                          const folder = folders.find(f => f.id === targetFolderId);
                          locationMessage = ` in folder "${folder?.name || driveMirrorFolderName || 'folder'}"`;
                        }
                        toast({
                          title: "Notes created",
                          description: `${successCount} note(s) created successfully${locationMessage}${failCount > 0 ? `. ${failCount} failed.` : '.'}`,
                        });
                      }

                      if (failCount > 0 && successCount === 0) {
                        toast({
                          title: "Creation failed",
                          description: `Failed to create ${failCount} note(s).`,
                          variant: "destructive",
                        });
                      }

                      // Hide loading after a short delay
                      setTimeout(() => {
                        setIsCreatingNotes(false);
                        setNoteCreationProgress(null);
                        setShowDrivePicker(true);
                      }, 2000);
                    }}
                  >
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      <SiGoogledrive className="h-4 w-4 mr-2" />
                      Add from Google Drive
                    </DropdownMenuItem>
                  </GoogleDrivePicker>
                )}
                {!showDrivePicker && isCreatingNotes && noteCreationProgress && (
                  <div className="px-2 py-1.5">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">Creating notes...</span>
                        <span className="text-muted-foreground">
                          {noteCreationProgress.completed} / {noteCreationProgress.total}
                        </span>
                      </div>
                      <Progress
                        value={(noteCreationProgress.completed / noteCreationProgress.total) * 100}
                        className="h-2"
                      />
                      <p className="text-xs text-muted-foreground truncate">
                        {noteCreationProgress.current}
                      </p>
                      {noteCreationProgress.failed.length > 0 && (
                        <p className="text-xs text-destructive">
                          Failed: {noteCreationProgress.failed.length} file(s)
                        </p>
                      )}
                      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Please wait...</span>
                      </div>
                    </div>
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </div>

      {librarySection === "ai-toolkit" ? (
        renderAIToolkitView()
      ) : librarySection === "blueprints" ? (
        renderBlueprintsView()
      ) : (
        <>
          {activeFilter === "experts" && (
            <div className="flex justify-start sm:justify-end mt-4">
              <Button 
                size="sm" 
                onClick={() => setExpertDialogOpen(true)}
                className="gap-1 w-full sm:w-auto"
                data-testid="button-add-expert"
              >
                <Plus className="h-4 w-4" />
                Add Expert
              </Button>
            </div>
          )}

          {/* DS EDIT 5aug26 — icon-only search/sort + view toggles on ONE row */}
          <div className="flex items-center gap-1.5 mt-4 min-w-0">
            <IconSearch
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search library..."
              testId="input-search-library"
              endSlot={
                <VoiceInputButton
                  onTranscript={(text) => setSearchQuery(prev => prev ? `${prev} ${text}` : text)}
                  className="h-7 w-7"
                />
              }
            />
            <div className="flex flex-1 items-center justify-end gap-1 min-w-0">
            <IconSelectMenu
              label="Sort library"
              value={`${sortField}-${sortDirection}`}
              onValueChange={(value) => {
                const [field, direction] = value.split("-") as [LibrarySortField, "asc" | "desc"];
                setSortField(field);
                setSortDirection(direction);
              }}
              testId="select-sort-library"
              options={[
                {value: "title-asc", label: "A → Z", icon: ArrowUp, testId: "sort-library-title-asc"},
                {value: "title-desc", label: "Z → A", icon: ArrowDown, testId: "sort-library-title-desc"},
                {value: "type-asc", label: "Type A → Z", icon: ArrowUp, testId: "sort-library-type-asc"},
                {value: "type-desc", label: "Type Z → A", icon: ArrowDown, testId: "sort-library-type-desc"},
                {value: "topic-asc", label: "Topic A → Z", icon: ArrowUp, testId: "sort-library-topic-asc"},
                {value: "topic-desc", label: "Topic Z → A", icon: ArrowDown, testId: "sort-library-topic-desc"},
                {value: "createdAt-desc", label: "Newest", icon: ArrowDown, testId: "sort-library-created-desc"},
                {value: "createdAt-asc", label: "Oldest", icon: ArrowUp, testId: "sort-library-created-asc"},
                {value: "updatedAt-desc", label: "Recent", icon: ArrowDown, testId: "sort-library-updated-desc"},
                {value: "updatedAt-asc", label: "Least recent", icon: ArrowUp, testId: "sort-library-updated-asc"},
              ]}
            />

            <div className="flex items-center gap-0.5 border rounded-md p-0.5">
              <Button
                variant={viewMode === "grid" ? "secondary" : "ghost"}
                size="icon"
                className="h-7 w-7"
                onClick={() => setViewMode("grid")}
                title="Grid view"
              >
                <LayoutGrid className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant={viewMode === "list" ? "secondary" : "ghost"}
                size="icon"
                className="h-7 w-7"
                onClick={() => setViewMode("list")}
                title="List view"
              >
                <List className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant={viewMode === "table" ? "secondary" : "ghost"}
                size="icon"
                className="h-7 w-7"
                onClick={() => setViewMode("table")}
                title="Table view"
              >
                <Table2 className="h-3.5 w-3.5" />
              </Button>
            </div>
            </div>
          </div>

          {selectedItems.size > 0 && activeFilter !== "tutorials" && (
            <div className="flex items-center gap-3 p-3 mt-4 bg-primary/5 border border-primary/20 rounded-lg">
              <Checkbox
                checked={selectedItems.size === filteredAndSortedItems.length && filteredAndSortedItems.length > 0}
                onCheckedChange={selectAllItems}
                data-testid="checkbox-select-all"
              />
              <span className="text-sm font-medium">{selectedItems.size} item(s) selected</span>
              <div className="flex-1" />
              <Button
                size="sm"
                variant="outline"
                data-testid="button-bulk-move"
                onClick={() => {
                  setMoveToFolderSearchQuery("");
                  setMoveToFolderDialogOpen(true);
                }}
              >
                <Folder className="h-4 w-4 mr-2" />
                Move to
              </Button>
              <Dialog open={moveToFolderDialogOpen} onOpenChange={setMoveToFolderDialogOpen}>
                <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col">
                  <DialogHeader>
                    <DialogTitle>Move to Folder, Space or Topic</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 flex flex-col min-h-0">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search folders, spaces, topics..."
                        value={moveToFolderSearchQuery}
                        onChange={(e) => setMoveToFolderSearchQuery(e.target.value)}
                        className="pl-8"
                        data-testid="input-move-folder-search"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => handleMoveToFolderAndClose(null)}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Remove from Folder
                    </Button>
                    <ScrollArea className="h-[320px] w-full border rounded-md">
                      <div className="p-2 space-y-3">
                        {moveFolderTreeFiltered.length === 0 && moveSpacesFiltered.length === 0 && moveTopicsFiltered.length === 0 ? (
                          <p className="text-sm text-muted-foreground py-4 text-center">
                            {moveToFolderSearchQuery.trim() ? "No folders, spaces or topics match your search." : "No folders, spaces or topics yet."}
                          </p>
                        ) : (
                          <>
                            {moveFolderTreeFiltered.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-2 pb-1">Folders</p>
                                {(function renderMoveTree(nodes: MoveFolderTreeNode[], depth: number) {
                                  return nodes.map((node) => {
                                    const hasChildren = node.children.length > 0;
                                    const isExpanded = expandedMoveFolderIds.has(node.id);
                                    return (
                                      <div key={`f-${node.id}`} className="rounded-md">
                                        <div
                                          className="flex items-center gap-1 py-1.5 px-2 rounded hover:bg-accent cursor-pointer group"
                                          style={{ paddingLeft: `${depth * 16 + 8}px` }}
                                        >
                                          <button
                                            type="button"
                                            className="p-0.5 rounded hover:bg-accent/80"
                                            onClick={(e) => { e.stopPropagation(); if (hasChildren) toggleExpandedMoveFolder(node.id); }}
                                            aria-label={isExpanded ? "Collapse" : "Expand"}
                                          >
                                            {hasChildren ? (isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : <span className="w-4 inline-block" />}
                                          </button>
                                          <button
                                            type="button"
                                            className="flex-1 flex items-center gap-2 text-left min-w-0"
                                            onClick={() => handleMoveToFolderAndClose(node.id)}
                                          >
                                            <FolderOpen className="h-4 w-4 text-amber-600 shrink-0" />
                                            <span className="truncate">{node.name}</span>
                                          </button>
                                        </div>
                                        {hasChildren && isExpanded && (
                                          <div>{renderMoveTree(node.children, depth + 1)}</div>
                                        )}
                                      </div>
                                    );
                                  });
                                })(moveFolderTreeFiltered, 0)}
                              </div>
                            )}
                            {moveSpacesFiltered.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-2 pb-1">Spaces</p>
                                {moveSpacesFiltered.map((space) => (
                                  <button
                                    key={`s-${space.id}`}
                                    type="button"
                                    className="w-full flex items-center gap-2 py-1.5 px-2 rounded hover:bg-accent text-left min-w-0"
                                    onClick={() => handleMoveToSpaceAndClose(space.id)}
                                  >
                                    <Brain className="h-4 w-4 text-purple-600 shrink-0" />
                                    <span className="truncate">{space.name}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                            {moveTopicsFiltered.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-2 pb-1">Topics</p>
                                {moveTopicsFiltered.map((topic) => (
                                  <button
                                    key={`t-${topic.id}`}
                                    type="button"
                                    className="w-full flex items-center gap-2 py-1.5 px-2 rounded hover:bg-accent text-left min-w-0"
                                    onClick={() => handleMoveToTopicAndClose(topic.id)}
                                  >
                                    <Layers className="h-4 w-4 text-blue-600 shrink-0" />
                                    <span className="truncate">{topic.name}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                </DialogContent>
              </Dialog>
              <Button size="sm" variant="destructive" onClick={handleBulkDelete} data-testid="button-bulk-delete">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
              <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="button-clear-selection">
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          <div ref={libraryMainSectionRef} className="flex-1 min-h-0 mt-6">
            {activeFilter === "tutorials" ? (
              tutorialVideosLoading ? (
                <div className="flex items-center justify-center py-24">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : filteredTutorialVideos.length === 0 ? (
                <EmptyState
                  icon={Video}
                  title={searchQuery.trim() ? "No tutorials match your search" : "No tutorials yet"}
                  description={
                    searchQuery.trim()
                      ? "Try a different search term or clear the search box."
                      : "Tutorial videos will appear here when your team adds them."
                  }
                />
              ) : (
                <ScrollArea className="h-full">
                  {viewMode === "grid" && renderTutorialGridView()}
                  {viewMode === "list" && renderTutorialListView()}
                  {viewMode === "table" && renderTutorialTableView()}
                </ScrollArea>
              )
            ) : !hasLibraryBrowseContent ? (
              <EmptyState
                icon={BookOpen}
                title={searchQuery ? "No results found" : selectedFolderId ? "This folder is empty" : "Your library is empty"}
                description={
                  searchQuery
                    ? "Try adjusting your search or filters"
                    : selectedFolderId
                    ? "Add notes, references, or create subfolders to organize your content"
                    : "Add notes or references to build your personal knowledge base"
                }
              />
            ) : (
              <ScrollArea className="h-full">
                <div className="pr-4 pb-6">
                  {librarySection === "content" && (
                    <LibraryBreadcrumbs
                      selectedFolderId={selectedFolderId}
                      folders={folders}
                      spaces={spaces}
                      topics={topics}
                      filterSpaceId={filterSpaceId}
                      filterTopicId={filterTopicId}
                      filterLibraryView={filterLibraryView}
                      onNavigateRoot={() => {
                        setSelectedFolderId(null);
                        setFilterSpaceId(null);
                        setFilterTopicId(null);
                        setFilterLibraryView(null);
                        navigate("/library");
                      }}
                      onClearLibraryView={() => {
                        setFilterLibraryView(null);
                        navigate("/library");
                      }}
                      onNavigateFolder={(folderId) => {
                        setSelectedFolderId(folderId);
                        navigate("/library");
                      }}
                      onNavigateSpace={(spaceId) => {
                        setSelectedFolderId(`space-${spaceId}`);
                        navigate("/library");
                      }}
                      onClearTopicFilter={() => {
                        setFilterTopicId(null);
                        setSelectedFolderId(null);
                        navigate("/library");
                      }}
                      onClearSpaceFilter={() => {
                        setFilterSpaceId(null);
                        navigate("/library");
                      }}
                    />
                  )}
                  {!filterLibraryView && !filterTopicId && !filterSpaceId && !isInSpacesFolder && !selectedSpaceId && (
                    <LibraryFolderStrip
                      folders={folderContainers}
                      folderItemCounts={folderItemCounts}
                      onFolderClick={handleFolderClick}
                      onNewSubfolder={openNewFolderDialog}
                      onEditFolder={openEditFolderDialog}
                      onDeleteFolder={handleDeleteFolderFromLibrary}
                    />
                  )}
                  {!filterLibraryView && !filterTopicId && spaceContainers.length > 0 && (
                    <LibrarySpacesSection
                      spaces={spaceContainers}
                      onSpaceClick={handleFolderClick}
                      title={isInSpacesFolder ? "Spaces" : "Spaces"}
                    />
                  )}
                  <LibraryDocumentsSection documentCount={documentItems.length}>
                    {documentItems.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4">
                        {searchQuery
                          ? "No documents match your search."
                          : filterLibraryView === "favorites"
                            ? "No favorites yet. Star a document from its menu to add it here."
                            : filterLibraryView === "recent"
                              ? "No recently opened documents."
                              : "No documents at this location."}
                      </p>
                    ) : (
                      <>
                        {renderDocumentsPanel()}
                      </>
                    )}
                  </LibraryDocumentsSection>
                </div>
              </ScrollArea>
            )}
          </div>
        </>
      )}


      <FilePreview
        open={filePreviewOpen}
        onOpenChange={setFilePreviewOpen}
        file={previewFile}
      />

      <Dialog
        open={!!tutorialPlaybackVideo}
        onOpenChange={(open) => {
          if (!open) setTutorialPlaybackVideo(null);
        }}
      >
        <DialogContent className="flex max-h-[min(92vh,900px)] w-[min(94vw,56rem)] max-w-[min(94vw,56rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[min(94vw,56rem)]">
          <DialogHeader className="space-y-1 border-b px-4 pb-3 pt-4 pr-12 text-left">
            <DialogTitle className="leading-snug">{tutorialPlaybackVideo?.title}</DialogTitle>
          </DialogHeader>
          <div className="bg-black">
            {tutorialPlaybackVideo && (
              <video
                key={tutorialPlaybackVideo.id}
                src={tutorialPlaybackVideo.url}
                controls
                autoPlay
                playsInline
                className="max-h-[min(78vh,calc(94vw*9/16))] w-full object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <PinVerificationDialog
        open={showPinDialogForDocument}
        onOpenChange={(open) => {
          if (!open) setPendingDocumentIdToOpen(null);
          setShowPinDialogForDocument(open);
        }}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["pin-status"] });
          if (pendingDocumentIdToOpen) {
            const item = unifiedItems.find((i) => i.id === pendingDocumentIdToOpen);
            if (item) {
              recordDocumentOpen({ id: item.id, itemType: item.itemType });
            }
            setIsCollapsed(false);
            setSelectedDocumentId(pendingDocumentIdToOpen);
            setPendingDocumentIdToOpen(null);
            setShowPinDialogForDocument(false);
          }
        }}
        title="Unlock document"
        description="Enter your PIN to open this document."
      />

      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-title">New Title</Label>
              <Input
                id="new-title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Enter new title..."
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleConfirmRename();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmRename} disabled={renameMutation.isPending || !newTitle.trim()}>
              {renameMutation.isPending ? "Renaming..." : "Rename"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Merge Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Select a document to merge "{mergeSourceItem?.title}" into. The content will be appended to the target document, and this document will be deleted.
            </p>
            <div className="space-y-2">
              <Label htmlFor="merge-target">Target Document</Label>
              <Select value={mergeTargetId} onValueChange={setMergeTargetId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a document..." />
                </SelectTrigger>
                <SelectContent>
                  {noteItems.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmMerge} disabled={mergeMutation.isPending || !mergeTargetId}>
              {mergeMutation.isPending ? "Merging..." : "Merge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RelationshipInspectorDrawer
        item={inspectorItem ? {
          ...inspectorItem,
          folderId: inspectorItem.folderId || null,
        } : null}
        open={inspectorOpen}
        onOpenChange={setInspectorOpen}
        onRelationshipsUpdated={() => {
          queryClient.invalidateQueries({ queryKey: ["/api/knowledge-base"] });
          queryClient.invalidateQueries({ queryKey: ["/api/sources"] });
          queryClient.invalidateQueries({ queryKey: ["/api/library-folders"] });
        }}
      />

      <QuickPromptModal
        prompt={selectedAITool?.tier === 'quick-prompt' ? selectedAITool as QuickPrompt : null}
        open={quickPromptModalOpen}
        onOpenChange={(open) => {
          setQuickPromptModalOpen(open);
          if (!open) setSelectedAITool(null);
        }}
        onExecute={handleQuickPromptExecute}
      />

      <WorkflowBuilderModal
        open={workflowBuilderOpen}
        onOpenChange={setWorkflowBuilderOpen}
        workflow={editingWorkflow}
        onDelete={async (workflowId) => {
          try {
            setIsLoadingWorkflows(true);
            setWorkflowsError(null);
            await aiWorkflowsService.deleteWorkflow(workflowId);
            toast({
              title: "Workflow Deleted",
              description: "The workflow has been deleted successfully.",
            });
            queryClient.invalidateQueries({ queryKey: ["/api/ai-workflows"] });
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Failed to delete workflow";
            setWorkflowsError(errorMessage);
            toast({
              title: "Error",
              description: errorMessage,
              variant: "destructive",
            });
          } finally {
            setIsLoadingWorkflows(false);
            setEditingWorkflow(null);
          }
        }}
        onSave={async (workflow) => {
          try {
            setIsLoadingWorkflows(true);
            setWorkflowsError(null);
            
            if (editingWorkflow && editingWorkflow.createdBy === 'user') {
              // Update existing user-created workflow
              await aiWorkflowsService.updateWorkflow(editingWorkflow.id, {
                name: workflow.name || "",
                description: workflow.description || "",
                icon: workflow.icon || "GitCompare",
                color: workflow.color || "bg-orange-500",
                category: workflow.category || "Automation",
                tags: workflow.tags || [],
                access: workflow.access || "free",
                trigger: workflow.trigger || {
                  type: "manual",
                  config: {}
                },
                nodes: workflow.nodes || [],
                edges: workflow.edges || [],
                entryNodeId: workflow.entryNodeId,
                variables: workflow.variables,
                isBlueprint: workflow.isBlueprint,
                blueprintCategory: workflow.blueprintCategory,
                estimatedDuration: workflow.estimatedDuration,
                estimatedCost: workflow.estimatedCost,
                lastRunAt: workflow.lastRunAt,
                runCount: workflow.runCount,
                status: workflow.status,
              });
              
              toast({
                title: "Workflow Updated",
                description: `"${workflow.name}" has been updated successfully.`,
              });
            } else {
              // Create new workflow
              const createdWorkflow = await aiWorkflowsService.createWorkflow({
                name: workflow.name || "",
                description: workflow.description || "",
                icon: workflow.icon || "GitCompare",
                color: workflow.color || "bg-orange-500",
                category: workflow.category || "Automation",
                tags: workflow.tags || [],
                access: workflow.access || "free",
                trigger: workflow.trigger || {
                  type: "manual",
                  config: {}
                },
                nodes: workflow.nodes || [],
                edges: workflow.edges || [],
                entryNodeId: workflow.entryNodeId,
                variables: workflow.variables,
                isBlueprint: workflow.isBlueprint,
                blueprintCategory: workflow.blueprintCategory,
                estimatedDuration: workflow.estimatedDuration,
                estimatedCost: workflow.estimatedCost,
                lastRunAt: workflow.lastRunAt,
                runCount: workflow.runCount,
                status: workflow.status || "draft",
              });
              
              if (createdWorkflow) {
                toast({
                  title: "Workflow Created",
                  description: `"${workflow.name}" has been created successfully.`,
                });
                queryClient.invalidateQueries({ queryKey: ["/api/ai-workflows"] });
                navigate(`/chat?workflow=${createdWorkflow.id}`);
              }
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Failed to save workflow";
            setWorkflowsError(errorMessage);
            toast({
              title: "Error",
              description: errorMessage,
              variant: "destructive",
            });
          } finally {
            setIsLoadingWorkflows(false);
            setWorkflowBuilderOpen(false);
            setEditingWorkflow(null);
          }
        }}
      />

      <Dialog open={expertDialogOpen} onOpenChange={setExpertDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-indigo-500" />
              Add Subject Matter Expert
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="expert-name">Name</Label>
              <Input
                id="expert-name"
                value={newExpertName}
                onChange={(e) => setNewExpertName(e.target.value)}
                placeholder="e.g., Dr. Jane Smith"
                data-testid="input-expert-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expert-role">Role / Expertise</Label>
              <Input
                id="expert-role"
                value={newExpertRole}
                onChange={(e) => setNewExpertRole(e.target.value)}
                placeholder="e.g., AI Researcher, Business Coach"
                data-testid="input-expert-role"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expert-bio">Bio / Notes (optional)</Label>
              <Input
                id="expert-bio"
                value={newExpertBio}
                onChange={(e) => setNewExpertBio(e.target.value)}
                placeholder="Brief background or key contributions..."
                data-testid="input-expert-bio"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpertDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddExpert} 
              disabled={createExpertMutation.isPending || !newExpertName.trim() || !newExpertRole.trim()}
              data-testid="button-save-expert"
            >
              {createExpertMutation.isPending ? "Adding..." : "Add Expert"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={childDocsDialogOpen} onOpenChange={setChildDocsDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookMarked className="h-5 w-5" />
              Documents by {selectedExpertItem?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Documents and resources associated with this expert. Add books, papers, or other materials they've authored.
            </p>
            {selectedExpertItem && (
              <div className="space-y-2">
                {getChildDocs(selectedExpertItem.id).length > 0 ? (
                  getChildDocs(selectedExpertItem.id).map((doc) => (
                    <div key={doc.id} className="flex items-center gap-2 p-2 border rounded-md">
                      {getTypeIcon(doc.type, doc.itemType, doc.tags, doc.url)}
                      <span className="flex-1">{doc.title}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No child documents yet. Tag documents with "parent:{selectedExpertItem.id}" to associate them.
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChildDocsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addReferenceDialogOpen} onOpenChange={setAddReferenceDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-blue-500" />
              Add Reference
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reference-url">URL</Label>
              <Input
                id="reference-url"
                value={newReferenceUrl}
                onChange={(e) => setNewReferenceUrl(e.target.value)}
                placeholder="https://example.com/article"
                data-testid="input-reference-url"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reference-title">Title (optional)</Label>
              <Input
                id="reference-title"
                value={newReferenceTitle}
                onChange={(e) => setNewReferenceTitle(e.target.value)}
                placeholder="A descriptive title..."
                data-testid="input-reference-title"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddReferenceDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddReference} 
              disabled={createReferenceMutation.isPending || !newReferenceUrl.trim()}
              data-testid="button-save-reference"
            >
              {createReferenceMutation.isPending ? "Adding..." : "Add Reference"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={newFolderDialogOpen} onOpenChange={(open) => {
        setNewFolderDialogOpen(open);
        if (!open) { setNewFolderName(""); setNewFolderDialogParentId(null); }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus className="h-5 w-5 text-amber-500" />
              {effectiveNewFolderParentId ? "New subfolder" : "New Folder"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {effectiveNewFolderParentId && (
              <p className="text-sm text-muted-foreground">
                Inside: <span className="font-medium">{folders.find(f => f.id === effectiveNewFolderParentId)?.name ?? "Folder"}</span>
              </p>
            )}
            <div className="space-y-2">
              <Label htmlFor="new-folder-name">Folder name</Label>
              <Input
                id="new-folder-name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Enter folder name..."
                onKeyDown={(e) => e.key === "Enter" && handleCreateFolderFromLibrary()}
                data-testid="input-new-folder-name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNewFolderDialogOpen(false); setNewFolderName(""); setNewFolderDialogParentId(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateFolderFromLibrary}
              disabled={createFolderMutation.isPending || !newFolderName.trim()}
              data-testid="button-create-folder"
            >
              {createFolderMutation.isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editFolderDialogOpen} onOpenChange={(open) => {
        setEditFolderDialogOpen(open);
        if (!open) { setEditingFolder(null); setEditFolderName(""); }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-amber-500" />
              Rename folder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-folder-name">Folder name</Label>
              <Input
                id="edit-folder-name"
                value={editFolderName}
                onChange={(e) => setEditFolderName(e.target.value)}
                placeholder="Enter folder name..."
                onKeyDown={(e) => e.key === "Enter" && handleUpdateFolderFromLibrary()}
                data-testid="input-edit-folder-name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditFolderDialogOpen(false); setEditingFolder(null); setEditFolderName(""); }}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateFolderFromLibrary}
              disabled={updateFolderMutation.isPending || !editFolderName.trim()}
              data-testid="button-save-rename-folder"
            >
              {updateFolderMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
